import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { safeNext } from "@/lib/return-to";
import { useEffect, useState } from "react";
import { usePublishPageLanguage } from "@/lib/page-language";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CONTACT_EMAIL } from "@/lib/contact";
import { changeAccountLocale } from "@/lib/locale.functions";
import ClassTab from "@/components/cabinet/ClassTab";
import ClassAdminPanel from "@/components/cabinet/ClassAdminPanel";
import SchoolSummaryPanel from "@/components/cabinet/SchoolSummaryPanel";
import { getSchoolProfile, type SchoolProfile } from "@/lib/school.functions";
import { useServerFn } from "@tanstack/react-start";
import {
  listClaimCandidates,
  claimSession,
  addChild,
  listChildren,
  recordConsent,
  getConsentState,
  createChildInvite,
  createGuardianInvite,
  getMyPath,
  listAchievements,
  addAchievement,
  updateAchievement,
  softDeleteAchievement,
  getCabinetOverview,
  claimRoleIntent,
  consumeSchoolAccess,
} from "@/lib/cabinet.functions";
import type {
  ClaimCandidate,
  ChildRow,
  PathSnapshot,
  Achievement,
  CabinetOverview,
  SchoolMembership,
} from "@/lib/cabinet.functions";
import {
  Sparkles,
  Award,
  Map as MapIcon,
  User,
  Users,
  GraduationCap,
  LogOut,
  FileText,
  Ticket,
  CheckCircle2,
  Clock,
  ChevronDown,
} from "lucide-react";
import { navSections, type NavKey } from "@/config/design-tokens";
import { BrandPuzzle, ProgressPuzzle } from "@/components/cabinet/BrandPuzzle";
import { EmptyState, amberPill, outlinePill } from "@/components/cabinet/CabinetCards";
import { skillLabels } from "@/config/skill-labels";
import { StudentsTab } from "@/components/cabinet/StudentsTab";
import { LegalNameCard } from "@/components/cabinet/LegalNameCard";
import { isMinorBand } from "@/config/consent";
import { deleteMyAccount } from "@/lib/account-deletion.functions";
import { getEduAvailability } from "@/lib/edu-availability";
import {
  getOwnerTiles,
  startOwnerEduTest,
  startOwnerTest,
  type OwnerTiles,
} from "@/lib/owner-tiles.functions";
import { listSchools } from "@/lib/school-admin.functions";
import { isAccountLocale, type AccountLocale } from "@/lib/locale";
import { ABROAD_ENTRY_COPY, ABROAD_GUEST_START_PATH } from "@/edu/guest";

// lucide icon components keyed by the render-agnostic string in design-tokens.
const NAV_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  Award,
  Map: MapIcon,
  User,
  Users,
  GraduationCap,
};

// A nav entry. School tabs are added at runtime (one per membership) with keys
// like "school:<id>", so the key is a plain string here, not the fixed NavKey.
type CabinetTab = { key: string; icon: string; label: Record<Lang, string> };

// Cabinet route — separate from /admin. Passwordless magic-link entry.
// Phase A shell: shows login when anonymous, a minimal home when signed in.
// «Мой путь» content lands in later PRs (M7 snapshot / M8 roadmap).
export const Route = createFileRoute("/cabinet")({
  // ?lang carries the site language the visitor arrived in (marketing links it
  // explicitly on all three landings); ?role is the pre-existing role intent
  // from the landing menu. Both are read from window.location at the point of
  // use — declaring them here only makes them addressable from typed <Link>s.
  // ?next — куда вернуть после входа (src/lib/return-to.ts): только путь
  // внутри кабинета.
  validateSearch: (search: Record<string, unknown>): { lang?: AccountLocale; role?: string; next?: string } => ({
    lang: isAccountLocale(search.lang) ? search.lang : undefined,
    role: typeof search.role === "string" ? search.role : undefined,
    next: safeNext(search.next) ?? undefined,
  }),
  head: () => ({
    meta: [{ title: "Кабинет — Yolutap" }, { name: "robots", content: "noindex" }],
    // Brand fonts (headings = Source Serif 4, UI = Nunito Sans). Same loader the
    // careers route uses; /cabinet didn't load them before D1.
    links: [
    ],
  }),
  component: CabinetPage,
});

// Narrow centered shell — login + loading only. The signed-in cabinet uses the
// full-width AppShell below (D1 removed the old blanket max-w-md wrapper).
function CenteredShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-yt-cream text-yt-ink font-yt-sans flex items-center justify-center p-4">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}

// Signed-in cabinet layout. Mobile-first: content full-width with 16px gutters
// and a bottom tab bar (thumb-reachable, safe-area aware); from md a top bar
// replaces it and the container caps at ~1120px. Both bars are built from the
// single navSections list in design-tokens.
function AppShell({
  lang,
  active,
  sections,
  onTab,
  onSignOut,
  signingOut,
  signOutLabel,
  onLangChange,
  children,
}: {
  lang: Lang;
  active: string;
  sections: readonly CabinetTab[];
  onTab: (k: string) => void;
  onSignOut: () => void;
  signingOut: boolean;
  signOutLabel: string;
  onLangChange: (l: Lang) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-yt-cream text-yt-ink font-yt-sans">
      {/* Desktop top bar */}
      <header className="hidden md:block sticky top-0 z-20 border-b border-yt-line bg-yt-cream/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center gap-2 px-6">
          <span className="mr-4 font-yt-serif text-lg font-semibold text-yt-teal">Yolutap</span>
          <nav className="flex items-center gap-1">
            {sections.map((s) => {
              const Icon = NAV_ICONS[s.icon];
              const on = active === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => onTab(s.key)}
                  aria-current={on ? "page" : undefined}
                  aria-label={s.label[lang]}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors hover:bg-yt-teal/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yt-teal focus-visible:ring-offset-2 ${
                    on ? "bg-yt-teal/10 text-yt-teal" : "text-yt-ink/60"
                  }`}
                >
                  {Icon ? <Icon className="size-4" /> : null}
                  {s.label[lang]}
                </button>
              );
            })}
          </nav>
          {/* On every screen, not on a settings page. */}
          <LanguagePicker lang={lang} onChange={onLangChange} className="ml-auto" />
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            aria-label={signOutLabel}
            className="flex items-center gap-2 rounded-full px-4 py-2 text-sm text-yt-ink/60 transition-colors disabled:opacity-50"
          >
            <LogOut className="size-4" />
            {signingOut ? "…" : signOutLabel}
          </button>
        </div>
      </header>

      {/* Content — bottom padding leaves room for the mobile tab bar. */}
      <main className="mx-auto max-w-[1120px] px-4 pb-28 pt-4 md:px-6 md:pb-10 md:pt-8">
        {/* Mobile has no top bar — only the bottom tab row — so the switch
            rides above the content, where it is visible on the FIRST screen
            without opening anything. */}
        <div className="mb-3 flex justify-end md:hidden">
          <LanguagePicker lang={lang} onChange={onLangChange} />
        </div>
        {children}
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-yt-line bg-yt-cream md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Cabinet"
      >
        {/* Columns follow the section count — grid-cols-4 was hardcoded, so the
            fifth tab wrapped onto its own row and doubled the bar's height.
            Derived, so adding or removing a tab can't reintroduce that. */}
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))` }}
        >
          {sections.map((s) => {
            const Icon = NAV_ICONS[s.icon];
            const on = active === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => onTab(s.key)}
                aria-current={on ? "page" : undefined}
                aria-label={s.label[lang]}
                className={`flex min-h-[56px] flex-col items-center justify-center gap-1 px-1 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-yt-teal ${
                  on ? "text-yt-teal" : "text-yt-ink/55"
                }`}
              >
                {Icon ? <Icon className="size-5 shrink-0" /> : null}
                {/* Truncate, never wrap: a long label must lose its tail, not
                    push the bar to a second line. The full text stays in
                    aria-label above. */}
                <span className="w-full truncate text-center leading-none">{s.label[lang]}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// Cabinet i18n. Local trilingual dictionary (kept colocated for now; can be
// folded into src/lib/i18n.ts later). Post-login language = the user's
// profiles.locale, falling back to the browser language, then RU. The pre-login
// MagicLinkLogin card stays RU — there's no session/profile to read yet.
type Lang = "ru" | "az" | "en";
function normLang(l: string | null | undefined): Lang {
  return l === "az" || l === "en" ? l : "ru";
}
const CT: Record<Lang, Record<string, string>> = {
  ru: {
    title: "Мой кабинет",
    logged_in: "Вы вошли как",
    sign_out: "Выйти",
    language_title: "Язык кабинета",
    language_failed: "Не удалось сменить язык. Попробуйте ещё раз.",
    tab_soon: "Появится в этом обновлении кабинета.",
    greeting: "Привет",
    progress_suffix: "из 4 готово",
    school_access_title: "Доступ для школ — по договору",
    school_access_body: `Кабинеты учителя и директора открывает школа: мы заводим их после договора. Напишите нам на ${CONTACT_EMAIL} — и мы всё настроим.`,
    school_notice_closed_title: "Приглашение от школы у нас есть",
    school_notice_closed_body:
      "Школа ещё не открыта с нашей стороны. Как только откроем — ваш школьный раздел появится сам, входить заново не нужно.",
    school_notice_stuck_title: "Приглашение есть, но доступ не открылся",
    school_notice_stuck_body: `Это на нашей стороне, мы уже видим это в логах. Напишите нам на ${CONTACT_EMAIL}, если раздел не появится после обновления страницы.`,
    school_notice_unreachable_title: "Не удалось проверить школьный доступ",
    school_notice_unreachable_body:
      "Обновите страницу. Если школьный раздел не появится — напишите нам, это не ваша ошибка.",
    cta_take_test: "Пройти тест",
    welcome: "Добро пожаловать!",
    puzzle_test: "тест",
    puzzle_achievements: "награды",
    puzzle_path: "путь",
    puzzle_school: "школа",
    test_entry_title: "Профориентационный тест",
    test_entry_body: "20–30 минут. Результат сразу после прохождения.",
    empty_results_title: "Здесь появится твой результат",
    empty_results_body:
      "Пройди тест — и увидишь свой профиль: сильные стороны и карьерные направления.",
    empty_ach_title: "Собери свои награды",
    empty_ach_body: "Дипломы, сертификаты, олимпиады — всё в одном месте.",
    empty_ach_cta: "Загрузить диплом",
    empty_path_title: "Твой путь впереди",
    empty_path_body: "После теста здесь появится план: предметы, навыки и шаги к цели.",
    school_card_title: "Есть код класса?",
    // Тут стояло «личный отчёт — только с твоего согласия». Кнопки согласия
    // нет и не будет: владелец решил проще — отчёт видят ученик и его учитель.
    // Обещание, которого система не держит, хуже прямого правила, и хуже
    // именно на этом экране: его читают перед тем, как согласиться.
    school_card_body: "Введи код — и присоединишься к классу. Отчёт видите ты и твой учитель.",
    school_card_cta: "Ввести код класса",
    results_count: "результатов",
    kids_here: "Ваши дети",
    kids_report: "Отчёт по подбору →",
    kids_manage: "Управление на «Профиле»",
    claimed_new: "Ребёнок {name} добавлен, результат теста ({age}) привязан к нему.",
    claimed_merged: "Результат теста ({age}) привязан к ребёнку {name} — он у вас уже был.",
    age_label: "Возрастная группа",
    age_hint: "Нужна для подбора: по ней считаются возрастные пороги стипендий.",
    children_empty: "Пока никого. Добавьте ребёнка, чтобы видеть его профиль.",
    open_report: "Открыть отчёт",
    mypath: "«Мой путь»",
    loading_results: "Загрузка результатов…",
    mypath_empty:
      "Пока пусто. Пройдите тест — результат появится здесь. Привязка прошлых результатов — скоро.",
    result: "Результат",
    years: "лет",
    claim_title: "Нашли ваши прошлые результаты",
    claim_body: "Тесты, оформленные на вашу почту. Добавить их в кабинет?",
    add: "Добавить",
    status_done: "завершён",
    status_not_done: "не завершён",
    more_show: "ещё {n}",
    more_hide: "свернуть",
    children: "Дети",
    add_child: "Добавить ребёнка",
    child_name: "Имя ребёнка",
    save: "Сохранить",
    cancel: "Отмена",
    consent_title: "Согласие на обработку данных",
    consent_body:
      "Продолжая, вы соглашаетесь с обработкой данных и политикой конфиденциальности Yolutap. (Текст согласия — черновик, финальная версия появится позже.)",
    consent_accept: "Принять",
    invite: "Пригласить ребёнка",
    invite_copy: "Скопировать ссылку-приглашение",
    invite_copied: "Скопировано",
    invite_hint: "Отправьте ссылку ребёнку в мессенджере — он войдёт своим email.",
    invite_consent_needed: "Сначала — согласие на обработку данных ребёнка.",
    invite_consent_btn: "Дать согласие",
    invite_new: "Создать новую ссылку",
    badge_linked: "привязан",
    badge_parent_linked: "родитель привязан",
    guardian_section_title: "Родитель",
    invite_parent: "Пригласить родителя",
    guardian_desc: "Пусть родитель подтвердит и увидит твой профиль.",
    guardian_hint: "Отправь ссылку родителю — она откроется на его телефоне.",
    ach_title: "Достижения",
    ach_add: "Добавить достижение",
    ach_empty: "Пока пусто. Добавьте первый сертификат, олимпиаду или курс.",
    ach_kind: "Тип",
    ach_name: "Название",
    ach_issuer: "Кто выдал",
    ach_date: "Дата",
    ach_result: "Результат / балл",
    ach_delete: "Удалить",
  },
  az: {
    title: "Kabinetim",
    logged_in: "Daxil oldunuz:",
    sign_out: "Çıxış",
    language_title: "Kabinetin dili",
    language_failed: "Dili dəyişmək alınmadı. Yenidən cəhd edin.",
    tab_soon: "Bu yeniləmədə burada olacaq.",
    greeting: "Salam",
    progress_suffix: "/ 4 tamamlandı",
    school_access_title: "Məktəblər üçün giriş — müqavilə ilə",
    school_access_body: `Müəllim və direktor kabinetlərini məktəb açır: onları müqavilədən sonra yaradırıq. ${CONTACT_EMAIL} ünvanına yazın — hər şeyi quraşdıraq.`,
    school_notice_closed_title: "Məktəbdən dəvətiniz bizdədir",
    school_notice_closed_body:
      "Məktəb hələ bizim tərəfimizdən açılmayıb. Açan kimi məktəb bölməniz özü görünəcək — yenidən daxil olmaq lazım deyil.",
    school_notice_stuck_title: "Dəvət var, amma giriş açılmadı",
    school_notice_stuck_body: `Bu bizim tərəfimizdədir, artıq loglarda görürük. Səhifəni yeniləyəndən sonra bölmə görünməsə, ${CONTACT_EMAIL} ünvanına yazın.`,
    school_notice_unreachable_title: "Məktəb girişini yoxlamaq alınmadı",
    school_notice_unreachable_body:
      "Səhifəni yeniləyin. Məktəb bölməsi görünməsə — bizə yazın, bu sizin səhviniz deyil.",
    cta_take_test: "Testi keç",
    welcome: "Xoş gəldin!",
    puzzle_test: "test",
    puzzle_achievements: "nailiyyət",
    puzzle_path: "yol",
    puzzle_school: "məktəb",
    test_entry_title: "Karyera testi",
    test_entry_body: "20–30 dəqiqə. Nəticə testdən dərhal sonra.",
    empty_results_title: "Nəticən burada görünəcək",
    empty_results_body: "Testi keç — güclü tərəflərini və karyera istiqamətlərini gör.",
    empty_ach_title: "Nailiyyətlərini topla",
    empty_ach_body: "Diplomlar, sertifikatlar, olimpiadalar — hamısı bir yerdə.",
    empty_ach_cta: "İlk diplomunu yüklə",
    empty_path_title: "Yolun qarşıdadır",
    empty_path_body: "Testdən sonra burada plan görünəcək: fənlər, bacarıqlar və hədəfə addımlar.",
    school_card_title: "Sinif kodun var?",
    school_card_body: "Kodu daxil et — sinfə qoşul. Hesabatı sən və müəllimin görürsünüz.",
    school_card_cta: "Sinif kodunu daxil et",
    results_count: "nəticə",
    kids_here: "Uşaqlarınız",
    kids_report: "Seçim hesabatı →",
    kids_manage: "İdarəetmə «Profil» bölməsində",
    claimed_new: "{name} əlavə edildi, test nəticəsi ({age}) ona bağlandı.",
    claimed_merged: "Test nəticəsi ({age}) artıq mövcud olan {name} adlı uşağa bağlandı.",
    age_label: "Yaş qrupu",
    age_hint: "Seçim üçün lazımdır: təqaüd yaş həddi bununla hesablanır.",
    children_empty: "Hələ heç kim yoxdur. Profilini görmək üçün uşaq əlavə edin.",
    open_report: "Hesabatı aç",
    mypath: "«Mənim yolum»",
    loading_results: "Nəticələr yüklənir…",
    mypath_empty:
      "Hələ boşdur. Testi keçin — nəticə burada görünəcək. Keçmiş nəticələrin bağlanması tezliklə.",
    result: "Nəticə",
    years: "yaş",
    claim_title: "Keçmiş nəticələrinizi tapdıq",
    claim_body: "Poçtunuza rəsmiləşdirilmiş testlər. Kabinetə əlavə edilsin?",
    add: "Əlavə et",
    status_done: "tamamlandı",
    status_not_done: "yarımçıq",
    more_show: "daha {n}",
    more_hide: "gizlət",
    children: "Uşaqlar",
    add_child: "Uşaq əlavə et",
    child_name: "Uşağın adı",
    save: "Yadda saxla",
    cancel: "Ləğv et",
    consent_title: "Məlumatların emalına razılıq",
    consent_body:
      "Davam etməklə Yolutap-ın məlumat emalı və məxfilik siyasəti ilə razılaşırsınız. (Razılıq mətni qaralamadır, yekun versiya sonra əlavə olunacaq.)",
    consent_accept: "Qəbul edirəm",
    invite: "Uşağı dəvət et",
    invite_copy: "Dəvət linkini kopyala",
    invite_copied: "Kopyalandı",
    invite_hint: "Linki messencerdə uşağa göndərin — o, öz email-i ilə daxil olacaq.",
    invite_consent_needed: "Əvvəlcə — uşağın məlumatlarının emalına razılıq.",
    invite_consent_btn: "Razılıq ver",
    invite_new: "Yeni link yarat",
    badge_linked: "bağlı",
    badge_parent_linked: "valideyn bağlı",
    guardian_section_title: "Valideyn",
    invite_parent: "Valideyni dəvət et",
    guardian_desc: "Valideynin təsdiqləsin və profilini görsün.",
    guardian_hint: "Linki valideyninə göndər — o, telefonunda açılacaq.",
    ach_title: "Nailiyyətlər",
    ach_add: "Nailiyyət əlavə et",
    ach_empty: "Hələ boşdur. İlk sertifikat, olimpiada və ya kursu əlavə edin.",
    ach_kind: "Növ",
    ach_name: "Ad",
    ach_issuer: "Kim verib",
    ach_date: "Tarix",
    ach_result: "Nəticə / bal",
    ach_delete: "Sil",
  },
  en: {
    title: "My cabinet",
    logged_in: "Signed in as",
    sign_out: "Sign out",
    language_title: "Cabinet language",
    language_failed: "Could not change the language. Try again.",
    tab_soon: "Coming in this cabinet update.",
    greeting: "Hi",
    progress_suffix: "of 4 done",
    school_access_title: "School access is by agreement",
    school_access_body: `Teacher and director cabinets are opened by the school — we set them up once an agreement is in place. Write to us at ${CONTACT_EMAIL} and we’ll arrange it.`,
    school_notice_closed_title: "We have your school invitation",
    school_notice_closed_body:
      "The school is not open on our side yet. The moment it is, your school section appears on its own — no need to sign in again.",
    school_notice_stuck_title: "The invitation is here, the access did not open",
    school_notice_stuck_body: `This is on our side and we can already see it in the logs. Write to us at ${CONTACT_EMAIL} if the section does not appear after a refresh.`,
    school_notice_unreachable_title: "Could not check your school access",
    school_notice_unreachable_body:
      "Refresh the page. If the school section does not appear, write to us — this is not your mistake.",
    cta_take_test: "Take the test",
    welcome: "Welcome!",
    puzzle_test: "test",
    puzzle_achievements: "awards",
    puzzle_path: "path",
    puzzle_school: "school",
    test_entry_title: "Career-orientation test",
    test_entry_body: "20–30 minutes. Your result appears right after.",
    empty_results_title: "Your result will appear here",
    empty_results_body: "Take the test to see your profile: strengths and career directions.",
    empty_ach_title: "Collect your awards",
    empty_ach_body: "Diplomas, certificates, olympiads — all in one place.",
    empty_ach_cta: "Upload a diploma",
    empty_path_title: "Your path ahead",
    empty_path_body:
      "After the test, a plan appears here: subjects, skills and steps to your goal.",
    school_card_title: "Have a class code?",
    school_card_body: "Enter it to join your class. You and your teacher see the report.",
    school_card_cta: "Enter class code",
    results_count: "results",
    kids_here: "Your children",
    kids_report: "Matching report →",
    kids_manage: "Manage them under “Profile”",
    claimed_new: "{name} was added, and the test result ({age}) is now linked to them.",
    claimed_merged: "The test result ({age}) was linked to {name} — that child already existed.",
    age_label: "Age group",
    age_hint: "Needed for matching: scholarship age caps are computed from it.",
    children_empty: "No one yet. Add a child to see their profile.",
    open_report: "Open report",
    mypath: "“My path”",
    loading_results: "Loading results…",
    mypath_empty:
      "Empty for now. Take the test — your result will appear here. Linking past results is coming soon.",
    result: "Result",
    years: "years",
    claim_title: "We found your past results",
    claim_body: "Tests registered to your email. Add them to your cabinet?",
    add: "Add",
    status_done: "completed",
    status_not_done: "not completed",
    more_show: "{n} more",
    more_hide: "hide",
    children: "Children",
    add_child: "Add child",
    child_name: "Child’s name",
    save: "Save",
    cancel: "Cancel",
    consent_title: "Data processing consent",
    consent_body:
      "By continuing you agree to Yolutap’s data processing and privacy policy. (Consent text is a draft; the final version will follow.)",
    consent_accept: "Accept",
    invite: "Invite child",
    invite_copy: "Copy invite link",
    invite_copied: "Copied",
    invite_hint: "Send the link to your child via a messenger — they sign in with their own email.",
    invite_consent_needed: "First — consent to process the child’s data.",
    invite_consent_btn: "Give consent",
    invite_new: "Create new link",
    badge_linked: "linked",
    badge_parent_linked: "parent linked",
    guardian_section_title: "Parent",
    invite_parent: "Invite parent",
    guardian_desc: "Let your parent confirm and see your profile.",
    guardian_hint: "Send the link to your parent — it opens on their phone.",
    ach_title: "Achievements",
    ach_add: "Add achievement",
    ach_empty: "Empty for now. Add your first certificate, olympiad or course.",
    ach_kind: "Type",
    ach_name: "Title",
    ach_issuer: "Issuer",
    ach_date: "Date",
    ach_result: "Result / score",
    ach_delete: "Delete",
  },
};

// Achievement kind labels (ru/az/en). Keys match the DB check constraint.
const KIND_LABELS: Record<Lang, Record<string, string>> = {
  ru: {
    certificate: "Сертификат",
    olympiad: "Олимпиада",
    internship: "Стажировка",
    course: "Курс",
    project: "Проект",
    volunteering: "Волонтёрство",
    language_exam: "Языковой экзамен",
    award: "Награда",
    other: "Другое",
  },
  az: {
    certificate: "Sertifikat",
    olympiad: "Olimpiada",
    internship: "Təcrübə",
    course: "Kurs",
    project: "Layihə",
    volunteering: "Könüllülük",
    language_exam: "Dil imtahanı",
    award: "Mükafat",
    other: "Digər",
  },
  en: {
    certificate: "Certificate",
    olympiad: "Olympiad",
    internship: "Internship",
    course: "Course",
    project: "Project",
    volunteering: "Volunteering",
    language_exam: "Language exam",
    award: "Award",
    other: "Other",
  },
};
const KINDS = [
  "certificate",
  "olympiad",
  "internship",
  "course",
  "project",
  "volunteering",
  "language_exam",
  "award",
  "other",
] as const;

// profiles isn't in the generated Database types (added via SQL) — narrow cast.
type ProfileQuery = {
  select: (c: string) => ProfileQuery;
  eq: (c: string, v: string) => ProfileQuery;
  maybeSingle: () => Promise<{
    data: { locale?: string | null; default_role?: string | null } | null;
    error: unknown;
  }>;
};
function useCabinetProfile(userId: string): {
  lang: Lang;
  role: string;
  setLang: (l: Lang) => void;
} {
  // Start at RU (stable for SSR/hydration); resolved on the client. role gates
  // parent-only controls.
  const [lang, setLang] = useState<Lang>("ru");
  const [role, setRole] = useState<string>("parent");
  const applyIntent = useServerFn(claimRoleIntent);
  useEffect(() => {
    let active = true;
    // ?lang= WINS over profiles.locale for this visit. We send school links as
    // ?lang=az, and the person opening one has to land in an Azerbaijani
    // cabinet whatever their profile was filed under years ago — otherwise the
    // language of the letter and the language of the screen it opens disagree.
    // It is a display override and is deliberately NOT written to the profile:
    // arriving through a link is not a decision about your account. The switch
    // is the decision, and it writes.
    const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
    const urlParam = url?.searchParams.get("lang") ?? null;
    // normLang() answers "ru" for anything it does not know, so an explicit
    // membership test is needed — otherwise ?lang=zz would silently pin the
    // cabinet to Russian and outrank the profile.
    const urlLang = urlParam === "az" || urlParam === "ru" || urlParam === "en" ? urlParam : null;
    // Applied before the profile read, not after: the round trip is visible,
    // and a screen that renders Russian and flips to Azerbaijani a beat later
    // is the same defect in slow motion.
    if (urlLang) setLang(urlLang);

    (async () => {
      // Role intent from the landing menu ("I arrived as a tutor"). The server
      // decides whether it applies — an established account is never rewritten
      // — so this is a request, not an instruction.
      const intent = url?.searchParams.get("role") ?? null;
      if (intent === "tutor") {
        await applyIntent({ data: { role: "tutor" } }).catch(() => undefined);
      }
      const q = (supabase as unknown as { from: (t: string) => ProfileQuery }).from("profiles");
      const { data } = await q.select("locale, default_role").eq("id", userId).maybeSingle();
      if (!active) return;
      // The URL already decided; the profile does not get to overrule it a
      // second later.
      if (!urlLang) {
        if (data?.locale) setLang(normLang(data.locale));
        else if (typeof navigator !== "undefined")
          setLang(normLang(navigator.language?.slice(0, 2)));
      }
      if (data?.default_role) setRole(String(data.default_role));
    })();
    return () => {
      active = false;
    };
  }, [userId]);

  // <html lang> follows the SAME value the interface renders with, and this is
  // the only place in the cabinet that writes it.
  //
  // RootShell derives the attribute from the URL — search param, then path
  // prefix, then `ru` — which is right for every public page and cannot work
  // here: the account language lives in profiles.locale, and the document
  // request carries no session (the token is in localStorage and is attached
  // only to server-function calls), so the server has nothing to read. Measured
  // on production: GET /cabinet answers `<html lang="ru">` whatever the account
  // is, while GET /cabinet?lang=az answers `az`.
  //
  // That is why the two paths disagreed. ?lang= reached RootShell and set the
  // attribute; an explicit choice moved React state that RootShell knows
  // nothing about, so the screen turned Azerbaijani above an <html lang="ru">
  // — and Chrome then offers to translate an Azerbaijani page "from Russian",
  // a screen reader reads it with Russian phonemes, and hyphenation follows
  // the wrong rules. Stripping ?lang on an explicit choice made it worse in
  // one direction: history.replaceState does not update router state, so the
  // attribute kept the language the person had just moved away from.
  //
  // The page DECLARES its language and RootShell sets the attribute
  // (src/lib/page-language.ts): a declared language outranks ?lang= in router
  // state, so the stale-?lang problem above is covered without writing to the
  // element behind React's back — a direct write and RootShell could each be
  // the last writer, depending on render order.
  usePublishPageLanguage(lang);

  // An EXPLICIT choice. Two things beyond moving the state:
  //
  // The screen turns over at once rather than on the next reload — re-reading
  // the profile instead would leave the old language up for the length of a
  // round trip, on the one press whose entire purpose is the language.
  //
  // And ?lang= is stripped from the address. Without that, a person who
  // followed a ?lang=az link and then pressed RU would get Azerbaijani back on
  // the next reload, with the switch pointing at Russian — the override would
  // outlive the decision it was meant to yield to. replaceState, so the back
  // button is not given a step that does nothing.
  const chooseLang = (next: Lang) => {
    setLang(next);
    if (typeof window === "undefined") return;
    const u = new URL(window.location.href);
    if (u.searchParams.has("lang")) {
      u.searchParams.delete("lang");
      window.history.replaceState({}, "", u.toString());
    }
  };

  return { lang, role, setLang: chooseLang };
}

function CabinetPage() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      // Уже вошёл и пришёл с ?next — сразу туда, а не на главную кабинета.
      const next = safeNext(new URL(window.location.href).searchParams.get("next"));
      if (data.session && next) {
        window.location.replace(next);
        return;
      }
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready) {
    return (
      <CenteredShell>
        <p className="text-center text-sm text-yt-ink/60">Загрузка…</p>
      </CenteredShell>
    );
  }

  // Signed-in cabinet brings its own full-width AppShell; login/loading stay in
  // the narrow CenteredShell.
  if (!session) {
    return (
      <CenteredShell>
        <MagicLinkLogin />
      </CenteredShell>
    );
  }

  return <CabinetHome session={session} />;
}

// Pre-login copy. This card used to be hard-coded Russian ("there's no profile
// to read yet") — which is true but was the wrong conclusion: the LANGUAGE OF
// ENTRY is known, it just wasn't being carried. It arrives as ?lang from the
// marketing pages and goes on to become the account's language.
const LT: Record<AccountLocale, Record<string, string>> = {
  ru: {
    check_mail: "Проверьте почту",
    sent_pre: "Мы отправили ссылку для входа на",
    sent_post: ". Откройте её на этом устройстве, чтобы войти в кабинет.",
    resend: "Отправить снова",
    title: "Вход в кабинет",
    subtitle: "Личный кабинет ученика Yolutap.",
    email: "Email",
    email_hint: "Пришлём ссылку для входа без пароля.",
    submit: "Получить ссылку",
    home: "← На главную",
    err_send: "Не удалось отправить ссылку",
  },
  az: {
    check_mail: "Poçtunuzu yoxlayın",
    sent_pre: "Giriş keçidini bu ünvana göndərdik:",
    sent_post: ". Kabinetə daxil olmaq üçün onu bu cihazda açın.",
    resend: "Yenidən göndər",
    title: "Kabinetə giriş",
    subtitle: "Yolutap şagird kabineti.",
    email: "Email",
    email_hint: "Şifrəsiz giriş üçün keçid göndərəcəyik.",
    submit: "Keçid al",
    home: "← Ana səhifəyə",
    err_send: "Keçidi göndərmək alınmadı",
  },
  en: {
    check_mail: "Check your email",
    sent_pre: "We sent a sign-in link to",
    sent_post: ". Open it on this device to enter your cabinet.",
    resend: "Send again",
    title: "Sign in to your cabinet",
    subtitle: "The Yolutap student cabinet.",
    email: "Email",
    email_hint: "We'll send a link — no password needed.",
    submit: "Get the link",
    home: "← Back to home",
    err_send: "Could not send the link",
  },
};

// Language of the login screen, and therefore of the account about to be
// created. ?lang is the real signal (marketing carries it on every "sign in"
// link). A direct visit — a bookmark, a typed URL — has no site language, so
// the browser's is the best guess available; anything unknown stays Russian,
// which is what the card did unconditionally before.
/** «&next=…» для ссылки из письма — только если путь возврата прошёл проверку. */
function nextParam(): string {
  if (typeof window === "undefined") return "";
  const next = safeNext(new URL(window.location.href).searchParams.get("next"));
  return next ? `&next=${encodeURIComponent(next)}` : "";
}

function entryLang(): AccountLocale {
  if (typeof window === "undefined") return "ru";
  const fromUrl = new URL(window.location.href).searchParams.get("lang");
  if (isAccountLocale(fromUrl)) return fromUrl;
  const nav = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : null;
  return isAccountLocale(nav) ? nav : "ru";
}

function MagicLinkLogin() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Resolved on the client only — SSR has no URL search or navigator, and a
  // mismatch between the server and first client render would hydrate-warn.
  const [lang, setLang] = useState<AccountLocale>("ru");
  useEffect(() => setLang(entryLang()), []);
  const t = LT[lang];
  const homeHref = lang === "az" ? "/az" : lang === "en" ? "/en" : "/";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          // The language rides the magic link so /auth/callback can write it
          // onto the account. Without it the round-trip through the mailbox
          // loses every trace of which site the person came from.
          // ?next — туда, откуда человек пришёл на вход (например, /cabinet/edu).
          emailRedirectTo: `${window.location.origin}/auth/callback?lang=${lang}${nextParam()}`,
        },
      });
      if (error) throw error;
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.err_send);
    } finally {
      setLoading(false);
    }
  };

  const cardCls = "rounded-[17px] border border-yt-line bg-white p-6 text-yt-ink sm:p-8";

  if (sent) {
    return (
      <div className={cardCls}>
        <div className="mb-5 flex justify-center">
          <BrandPuzzle />
        </div>
        <h1 className="text-center font-yt-serif text-2xl font-semibold">{t.check_mail}</h1>
        <p className="mt-3 text-center text-sm leading-relaxed text-yt-ink/70">
          {t.sent_pre} <span className="font-semibold text-yt-ink">{email}</span>
          {t.sent_post}
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mx-auto mt-5 block text-sm font-semibold text-yt-teal underline"
        >
          {t.resend}
        </button>
      </div>
    );
  }

  return (
    <div className={cardCls}>
      <div className="mb-5 flex justify-center">
        <BrandPuzzle />
      </div>
      <h1 className="text-center font-yt-serif text-2xl font-semibold">{t.title}</h1>
      <p className="mt-2 text-center text-sm text-yt-ink/60">{t.subtitle}</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="cabinet-email" className="block text-sm font-semibold text-yt-ink/70">
            {t.email}
          </label>
          <input
            id="cabinet-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
            className="w-full rounded-xl border border-yt-line bg-white px-4 py-3 text-base text-yt-ink outline-none transition focus:border-yt-teal focus:ring-2 focus:ring-yt-teal/20"
          />
          <p className="text-xs text-yt-ink/50">{t.email_hint}</p>
        </div>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <button
          type="submit"
          disabled={loading || !email}
          className="w-full rounded-full bg-yt-amber px-6 py-3 text-base font-bold text-yt-ink transition hover:brightness-[1.03] disabled:opacity-50"
        >
          {loading ? "…" : t.submit}
        </button>
        <div className="text-center">
          {/* Plain anchor, not <Link>: the landings at / , /az , /en are static
              HTML served outside the router. */}
          <a href={homeHref} className="text-xs font-medium text-yt-teal underline">
            {t.home}
          </a>
        </div>
      </form>
    </div>
  );
}

// Открыт ли модуль «Образование за рубежом». Пока справочник стран не
// опубликован, ссылок отсюда быть не должно: анкета без справочника приводит
// семью к пустому отчёту. Условие живёт на сервере (edu-availability.ts) и
// снимается само, когда владелец публикует первую страну.
function useEduModuleOpen(): boolean {
  const check = useServerFn(getEduAvailability);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let active = true;
    check({})
      .then((r) => {
        if (active) setOpen(r.open);
      })
      .catch(() => {
        if (active) setOpen(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return open;
}

// D1 — compact overview (greeting + archetype + progress) for the header.
function useCabinetOverview(refreshKey: number): CabinetOverview | null {
  const fetchOverview = useServerFn(getCabinetOverview);
  const [overview, setOverview] = useState<CabinetOverview | null>(null);
  useEffect(() => {
    let active = true;
    fetchOverview({ data: undefined as never })
      .then((r) => {
        if (active) setOverview(r);
      })
      .catch(() => {
        if (active) setOverview(null);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);
  return overview;
}

// Active school memberships, and the grant consumption that may create them.
// consumeSchoolAccess is written to be cheap on the hot path: it looks at
// memberships first and only sweeps pending grants when there are none, so a
// parent or student never pays for the school lookup.
// What to say when a sign-in did not open a school. Not a debug dump — three
// outcomes a human can act on:
//   invited_closed — we hold your invitation, the school is not open yet
//   invited_stuck  — we hold your invitation and something went wrong our end
//   unreachable    — the check itself never answered
// Everything else is null, which is the ordinary case for every parent and
// every child: nothing to say, nothing shown.
type SchoolNotice = "invited_closed" | "invited_stuck" | "unreachable" | null;

function useSchoolMemberships(): {
  memberships: SchoolMembership[];
  ready: boolean;
  notice: SchoolNotice;
} {
  const consume = useServerFn(consumeSchoolAccess);
  const [memberships, setMemberships] = useState<SchoolMembership[]>([]);
  const [ready, setReady] = useState(false);
  const [notice, setNotice] = useState<SchoolNotice>(null);
  useEffect(() => {
    let active = true;
    consume({ data: undefined as never })
      .then((r) => {
        if (!active) return;
        setMemberships(r.memberships);
        const d = r.diagnosis;
        // Only speak when this person was actually granted something and did
        // not get it. A pending grant nobody can act on is the whole point:
        // it is the difference between "you were never invited" and "you were
        // invited and the door did not open".
        if (d && r.memberships.length === 0 && d.grants_matched > 0) {
          setNotice(
            d.skipped.every((s) => s.reason === "school_closed")
              ? "invited_closed"
              : "invited_stuck",
          );
        } else if (d && (!d.email_seen || d.grants_error)) {
          // The address never reached the server, or the lookup itself failed.
          // Both used to return an empty list — the same answer as "you were
          // never invited", which is what sent an hour of diagnosis the wrong
          // way.
          setNotice("invited_stuck");
        }
        setReady(true);
      })
      .catch(() => {
        if (!active) return;
        // Still fail-open on LAYOUT — the personal cabinet stays whole — but no
        // longer fail-silent. A swallowed rejection here is indistinguishable
        // from a working check that found nothing, and that is exactly the
        // ambiguity that made this undiagnosable from the outside.
        setNotice("unreachable");
        setReady(true);
      });
    return () => {
      active = false;
    };
  }, []);
  return { memberships, ready, notice };
}

// Persona header — greeting + archetype chip + progress puzzle + (when the test
// isn't done yet) a "take the test" CTA. Soft brand gradient gives the top of
// the cabinet volume so an empty account never reads as broken.
function CabinetHeader({
  overview,
  lang,
  onTab,
}: {
  overview: CabinetOverview;
  lang: Lang;
  onTab: (k: NavKey) => void;
}) {
  const t = CT[lang];
  const navigate = useNavigate();
  const done = Object.values(overview.progress).filter(Boolean).length;
  const name = overview.displayName?.trim();
  const heading = name ? `${t.greeting}, ${name}!` : t.welcome;

  const puzzleLabels = {
    test: t.puzzle_test,
    achievements: t.puzzle_achievements,
    path: t.puzzle_path,
    school: t.puzzle_school,
  };
  // Each square routes to its part: an unfilled square leads to its action.
  const onPiece = (key: "test" | "achievements" | "path" | "school") => {
    if (key === "test") {
      if (overview.progress.test) onTab("results");
      else navigate({ to: "/start" });
    } else if (key === "achievements") onTab("achievements");
    else if (key === "path") onTab("path");
    else onTab("profile");
  };

  return (
    <section className="overflow-hidden rounded-[18px] border border-yt-line bg-gradient-to-br from-yt-teal/[0.14] via-yt-cream to-yt-amber/[0.18] p-5 md:p-7">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
        {/* Left: greeting + archetype + progress + (no-test) CTA */}
        <div className="min-w-0 flex-1">
          <h1 className="font-yt-serif text-2xl font-semibold leading-tight text-yt-ink [overflow-wrap:anywhere] md:text-3xl">
            {heading}
          </h1>
          {overview.archetype && (
            <span
              className="mt-2 inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold text-yt-ink"
              style={{ backgroundColor: `${overview.archetype.colorHex}22` }}
            >
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: overview.archetype.colorHex }}
              />
              <span className="truncate">{archetypeName(overview.archetype.code, lang)}</span>
            </span>
          )}
          <p className="mt-2 text-sm text-yt-ink/60">
            {done} {t.progress_suffix}
          </p>
          {/* The "take the test" CTA lives in the Results empty-state card below
              (in context) — not duplicated here. */}
        </div>

        {/* Right: labeled, tappable progress puzzle (given visual weight so the
            header fills its width). */}
        <div className="shrink-0 self-start sm:self-center">
          <ProgressPuzzle
            progress={overview.progress}
            labels={puzzleLabels}
            onPieceClick={onPiece}
          />
        </div>
      </div>
    </section>
  );
}

function HeaderSkeleton() {
  return (
    <div className="flex items-center gap-4 rounded-[18px] border border-yt-line bg-white p-5 md:gap-6 md:p-7">
      <div className="grid grid-cols-2 gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="size-[46px] animate-pulse rounded-[9px] bg-yt-line" />
        ))}
      </div>
      <div className="flex-1 space-y-2">
        <div className="h-7 w-40 animate-pulse rounded bg-yt-line" />
        <div className="h-4 w-24 animate-pulse rounded bg-yt-line" />
      </div>
    </div>
  );
}

// The school part of the cabinet, one instance per staff membership.
//
// The school's own name and logo sit at the top. It is the first thing they
// see, and a screen that carries their crest is theirs in a way a screen
// carrying only ours is not.
//
// What is BELOW the header is decided by role, and the three roles genuinely
// differ:
//
//   school_admin — the school's own administrator. Creates classes, assigns
//                  teachers, runs any class, reads the summary. The three
//                  people from the IT team asked for "bütün funksional".
//   counselor    — reads. The summary first, because it is the screen they
//                  came for; the rosters below it, without a single control
//                  that spends a seat. Their director asked, in writing, for
//                  "report view, analyzing" — LESS than the IT team — and
//                  handing him the same buttons would break that on day one.
//   teacher      — their own classes, and nothing about the school.
//
// The order matters as much as the contents. A director opening this tab must
// land on the aggregate picture, not scroll past a roster of five hundred
// names to reach it.
function SchoolTab({ lang, membership }: { lang: Lang; membership: SchoolMembership }) {
  const profile = useServerFn(getSchoolProfile);
  const [school, setSchool] = useState<SchoolProfile | null>(null);
  useEffect(() => {
    let alive = true;
    profile({ data: { school_id: membership.school_id } })
      .then((p) => alive && setSchool(p))
      // Fail-open: the header falls back to the name the membership already
      // carries, and the panels below fall back to the membership's own role.
      // A school losing its logo must not cost it its classes.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership.school_id]);

  const roleLabel: Record<string, Record<Lang, string>> = {
    teacher: { ru: "Учитель", az: "Müəllim", en: "Teacher" },
    school_admin: { ru: "Администратор школы", az: "Məktəb administratoru", en: "School admin" },
    counselor: { ru: "Психолог", az: "Psixoloq", en: "Counselor" },
  };
  const soon: Record<Lang, string> = {
    ru: "Классы и приглашения появятся здесь.",
    az: "Siniflər və dəvətlər burada görünəcək.",
    en: "Your classes and invitations will appear here.",
  };
  const reportsTitle: Record<Lang, string> = {
    ru: "Сводный отчёт",
    az: "Ümumiləşdirilmiş hesabat",
    en: "Summary report",
  };

  // Until the profile call answers, the membership's own role decides — the
  // same fact, from the row that put this tab on the screen in the first place.
  const canManage = school ? school.access.can_manage : membership.role === "school_admin";
  const canReadReports = school
    ? school.access.can_read_reports
    : membership.role === "school_admin" || membership.role === "counselor";
  const name = school?.name || membership.school_name || soon[lang];

  return (
    <div className="space-y-4 md:space-y-6">
      <section className="rounded-2xl border border-yt-line bg-white p-4 md:p-5">
        <div className="flex items-center gap-3">
          {school?.logo_url && (
            // Contained, never cropped: a school crest is not a decoration to
            // be trimmed to a circle. A load failure hides the image and leaves
            // the name — no broken-image icon on the school's own screen.
            <img
              src={school.logo_url}
              alt=""
              className="size-12 shrink-0 object-contain md:size-14"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <div className="min-w-0">
            <p className="font-yt-serif text-xl font-semibold text-yt-ink [overflow-wrap:anywhere]">
              {name}
            </p>
            <p className="mt-0.5 text-sm text-yt-ink/60">
              {roleLabel[membership.role]?.[lang] ?? membership.role}
            </p>
          </div>
        </div>
      </section>

      {/* The summary FIRST for whoever came to read it. For a director this is
          the screen — possibly the only one they ever open. */}
      {canReadReports && (
        <section className="space-y-3">
          <h3 className="font-yt-serif text-lg font-semibold text-yt-ink">{reportsTitle[lang]}</h3>
          <SchoolSummaryPanel
            schoolId={membership.school_id}
            lang={lang}
            archetypeLabel={(code) => archetypeName(code, lang)}
          />
        </section>
      )}

      {/* Passport name is asked HERE, on entering the school part — never on the
          way into the cabinet, so it can't stand between a parent and her
          child's results. */}
      <LegalNameCard lang={lang} />

      {canManage && <ClassAdminPanel schoolId={membership.school_id} lang={lang} />}

      {/* ClassTab owns both cases: the roster when this person may see a class
          here, and the calm "coming soon" line when they may not — a counselor
          or a director must not get an empty roster shell. Keeping the two in
          one component means one query, and the placeholder can never end up
          printed underneath a working roster. */}
      <ClassTab
        schoolId={membership.school_id}
        lang={lang}
        emptyNote={soon[lang]}
        isSchoolAdmin={canManage}
      />
    </div>
  );
}

function CabinetHome({ session }: { session: Session }) {
  const [signingOut, setSigningOut] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState<string | null>(null);
  const { lang, role, setLang } = useCabinetProfile(session.user.id);
  const { memberships, ready: schoolReady, notice: schoolNotice } = useSchoolMemberships();
  const overview = useCabinetOverview(refreshKey);
  const t = CT[lang];

  // Personal part (from default_role) — the tutor tab is ADDED for tutors,
  // never substituted, since a tutor is often a parent too.
  const personalSections = navSections.filter((s) => s.key !== "students" || role === "tutor");
  // School part (from active memberships) — one tab per school, added on top of
  // the personal part. Someone can be a parent and a teacher at once, or a
  // teacher at two schools; both are ordinary here.
  const schoolSections: CabinetTab[] = memberships.map((m) => ({
    key: `school:${m.school_id}`,
    icon: "GraduationCap",
    label: {
      ru: m.school_name ?? "Школа",
      az: m.school_name ?? "Məktəb",
      en: m.school_name ?? "School",
    },
  }));

  // A pure teacher — membership but no personal footprint — must not land on an
  // empty parent cabinet that looks broken. In that case the school tab leads
  // and the empty personal sections are not drawn. With both present, personal
  // leads: predictability over guessing intent.
  const hasPersonalContent =
    !!overview && (overview.counts.results > 0 || overview.counts.achievements > 0);
  const schoolOnly = memberships.length > 0 && schoolReady && !hasPersonalContent;

  const sections: CabinetTab[] = schoolOnly
    ? [...schoolSections, ...personalSections.filter((s) => s.key === "profile")]
    : [...personalSections, ...schoolSections];

  // Default tab resolves once the async membership check settles, so a pure
  // teacher opens on their school rather than flashing an empty "results".
  const defaultTab = schoolOnly ? schoolSections[0]?.key : "results";
  const active = tab ?? defaultTab ?? "results";

  const schoolIntent =
    typeof window !== "undefined" &&
    ["teacher", "director"].includes(new URL(window.location.href).searchParams.get("role") ?? "");

  const signOut = async () => {
    setSigningOut(true);
    await supabase.auth.signOut();
    // onAuthStateChange flips the view back to the login card.
  };

  // ВЛАДЕЛЕЦ ВИДИТ ТОЛЬКО ПЛИТКИ. user_roles.role = admin — выключатель, а не
  // место: главный экран /cabinet для него это три плитки и больше ничего,
  // остальные роли /cabinet не меняются. Проверка на клиенте — только выбор
  // вида; данные плиток защищены серверным assertAdmin в getOwnerTiles.
  const isAdmin = useIsAdmin(session.user.id);
  // null — роль ещё не прочитана: показываем пусто, а не чужой кабинет на миг.
  if (isAdmin === null) return <div className="min-h-screen bg-yt-cream" />;
  if (isAdmin) return <OwnerTilesScreen onSignOut={signOut} signingOut={signingOut} />;

  const activeSchool = memberships.find((m) => `school:${m.school_id}` === active);
  const activeLabel = sections.find((s) => s.key === active)?.label[lang] ?? "";

  return (
    <AppShell
      lang={lang}
      active={active}
      sections={sections}
      onTab={setTab}
      onSignOut={signOut}
      signingOut={signingOut}
      signOutLabel={t.sign_out}
      onLangChange={setLang}
    >
      {/* Results tab leads with the persona header (no separate "Результаты"
          heading — the header IS the top of this view). Other tabs keep a
          simple serif title. */}
      {active === "results" ? (
        <div className="space-y-4 md:space-y-6">
          {overview ? (
            <CabinetHeader overview={overview} lang={lang} onTab={setTab} />
          ) : (
            <HeaderSkeleton />
          )}
          {/* Amber belongs to the way into a test: that is the product's
              standing primary action, while claiming stray results is a
              one-off. So the banner asks whether anything else on this tab
              already paints one. MyPath no longer does — its empty-state CTA
              moved into TestEntry — leaving TestEntry the only source, and it
              answers from its own list rather than from a hardcoded true. */}
          <ClaimBanner
            lang={lang}
            onClaimed={() => setRefreshKey((k) => k + 1)}
            neighborAmber={testEntryRendersAmber()}
          />
          {/* Дети показываются ТАМ, ГДЕ ИХ СОЗДАЮТ. Раньше карточки жили
              только на «Профиле», а привязка теста происходила здесь: человек
              нажимал «Добавить», ничего не менялось на экране, и он нажимал
              ещё раз. Три нажатия — три привязанных теста и уверенность, что
              кабинет сломан. */}
          <ChildrenHere lang={lang} refreshKey={refreshKey} onTab={setTab} />
          <TestEntry lang={lang} />
          <MyPath refreshKey={refreshKey} lang={lang} />
        </div>
      ) : !activeSchool ? (
        <h1 className="mb-4 font-yt-serif text-2xl font-semibold text-yt-ink md:mb-6 md:text-3xl">
          {activeLabel}
        </h1>
      ) : null}

      {active === "achievements" && <AchievementsTab lang={lang} refreshKey={refreshKey} />}

      {active === "path" && (
        <EmptyState
          icon={MapIcon}
          title={t.empty_path_title}
          body={t.empty_path_body}
          action={
            <Link to="/start" className={amberPill}>
              {t.cta_take_test}
            </Link>
          }
        />
      )}

      {activeSchool && <SchoolTab lang={lang} membership={activeSchool} />}

      {/* A sign-in that did NOT open a school, and what we actually know about
          why. Three cases the person can act on; anything else stays silent.
          Shown regardless of ?role= — the teacher who arrives from a bare
          /cabinet link is the same person with the same problem. */}
      {schoolReady && schoolNotice && memberships.length === 0 && (
        <div className="mb-4 rounded-2xl border border-yt-line bg-white p-4 md:mb-6 md:p-5">
          <p className="font-yt-serif text-lg font-semibold text-yt-ink">
            {schoolNotice === "invited_closed"
              ? t.school_notice_closed_title
              : schoolNotice === "invited_stuck"
                ? t.school_notice_stuck_title
                : t.school_notice_unreachable_title}
          </p>
          <p className="mt-1 text-base text-yt-ink/70">
            {schoolNotice === "invited_closed"
              ? t.school_notice_closed_body
              : schoolNotice === "invited_stuck"
                ? t.school_notice_stuck_body
                : t.school_notice_unreachable_body}
          </p>
        </div>
      )}

      {/* Someone clicked "Teacher"/"Director", the check settled, and there is
          no invitation for this address at all. The honest answer beats a
          silent parent cabinet. Suppressed when the notice above already spoke
          — two cards about one subject read as a malfunction. */}
      {schoolIntent && schoolReady && !schoolNotice && memberships.length === 0 && (
        <div className="mb-4 rounded-2xl border border-yt-line bg-white p-4 md:mb-6 md:p-5">
          <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.school_access_title}</p>
          <p className="mt-1 text-base text-yt-ink/70">{t.school_access_body}</p>
        </div>
      )}

      {active === "students" && <StudentsTab lang={lang} />}

      {active === "profile" && (
        <ProfileTab
          session={session}
          lang={lang}
          role={role}
          refreshKey={refreshKey}
          onSignOut={signOut}
          signingOut={signingOut}
        />
      )}
    </AppShell>
  );
}

// B2 — account-level consent banner. Shows until the account holds CURRENT
// (matching POLICY_VERSION) ToS consent; "Accept" records it (journal + denorm).
// Fail-open on read error — never nag on a transient failure.
// B2 — ToS consent. Now CONTROLLED by ProfileTab (which owns the fetch/record so
// it can make consent the single primary action while pending). Presentational.
function ConsentGate({
  lang,
  onAccept,
  busy,
}: {
  lang: Lang;
  onAccept: () => void;
  busy: boolean;
}) {
  const t = CT[lang];
  return (
    <div className="rounded-2xl border border-yt-teal/25 bg-yt-teal/5 p-5">
      <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.consent_title}</p>
      <p className="mt-1 text-sm leading-relaxed text-yt-ink/60">{t.consent_body}</p>
      <button onClick={onAccept} disabled={busy} className={`${amberPill} mt-4`}>
        {busy ? "…" : t.consent_accept}
      </button>
    </div>
  );
}

// Account language — a control of the CHROME, not of a settings page.
//
// It used to live in the profile tab, which was wrong for the audience that
// needs it: a school whose staff all read Azerbaijani meets the cabinet in
// whatever language the account was first filed under, and has to find a
// settings screen in a language they do not read in order to leave it. It is
// on every screen now, in the top bar on desktop and above the content on
// mobile — the two places a person looks first.
//
// Codes, not names. "Azərbaycan / Русский / English" is right on a settings
// card and wrong in a header, where it is three long words competing with the
// navigation. AZ / RU / EN reads the same in all three languages and needs no
// translation, which is the same reason the full names were never translated
// either.
const LOCALE_CODES: Array<{ code: Lang; short: string; full: string }> = [
  { code: "az", short: "AZ", full: "Azərbaycan" },
  { code: "ru", short: "RU", full: "Русский" },
  { code: "en", short: "EN", full: "English" },
];

function LanguagePicker({
  lang,
  onChange,
  className = "",
}: {
  lang: Lang;
  onChange: (l: Lang) => void;
  className?: string;
}) {
  const t = CT[lang];
  const change = useServerFn(changeAccountLocale);
  const [busy, setBusy] = useState<Lang | null>(null);
  const [failed, setFailed] = useState(false);

  const pick = async (next: Lang) => {
    if (next === lang || busy) return;
    setBusy(next);
    setFailed(false);
    try {
      await change({ data: { locale: next } });
      onChange(next);
    } catch {
      // Said out loud. A switch that quietly does nothing is worse than no
      // switch: it gets pressed again, and again, and the product reads as
      // broken rather than the request as failed.
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={className}>
      <div
        role="group"
        aria-label={t.language_title}
        className="inline-flex items-center gap-1 rounded-full border border-yt-line bg-white p-0.5"
      >
        {LOCALE_CODES.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => void pick(l.code)}
            disabled={!!busy}
            aria-pressed={l.code === lang}
            // The full name is the accessible name — a screen reader saying
            // "AZ" helps nobody.
            aria-label={l.full}
            title={l.full}
            className={`min-h-[36px] min-w-[44px] rounded-full px-3 text-sm transition-colors disabled:opacity-50 ${
              l.code === lang
                ? "bg-yt-teal/10 font-bold text-yt-teal"
                : "text-yt-ink/55 hover:text-yt-ink"
            }`}
          >
            {busy === l.code ? "…" : l.short}
          </button>
        ))}
      </div>
      {failed && <p className="mt-1 text-xs font-semibold text-red-700">{t.language_failed}</p>}
    </div>
  );
}

// D1 — Profile tab. Owns consent state to enforce "one amber per screen": while
// ToS consent is pending it is the single primary (amber) action and everything
// else is secondary; once given, the class-code card becomes the primary.
function ProfileTab({
  session,
  lang,
  role,
  refreshKey,
  onSignOut,
  signingOut,
}: {
  session: Session;
  lang: Lang;
  role: string;
  refreshKey: number;
  onSignOut: () => void;
  signingOut: boolean;
}) {
  const t = CT[lang];
  const fetchState = useServerFn(getConsentState);
  const record = useServerFn(recordConsent);
  const [tosConsented, setTos] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    fetchState({ data: {} })
      .then((r) => {
        if (active) setTos(r.tosConsented);
      })
      .catch(() => {
        if (active) setTos(true); // fail-open — never nag on a transient error
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const pending = tosConsented === false;
  const accept = async () => {
    setBusy(true);
    try {
      await record({ data: { purpose: "tos", locale: lang } });
      setTos(true);
    } catch {
      // keep the gate on transient failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="-mt-2 text-xs text-yt-ink/45">
        {t.logged_in} <span className="text-yt-ink/70">{session.user.email}</span>
      </p>
      {pending && <ConsentGate lang={lang} onAccept={accept} busy={busy} />}
      {/* Renders itself only for tutors — the card decides, so no role check
          is duplicated here. */}
      <LegalNameCard lang={lang} />
      <ChildrenSection lang={lang} refreshKey={refreshKey} role={role} />
      <SchoolCodeCard lang={lang} primary={!pending} />
      <button
        type="button"
        onClick={onSignOut}
        disabled={signingOut}
        className="w-full rounded-full border border-yt-line bg-white px-5 py-2.5 text-sm font-semibold text-yt-ink transition hover:bg-yt-ink/5 disabled:opacity-50 md:hidden"
      >
        {signingOut ? "…" : t.sign_out}
      </button>
      {/* Last on the page, and the only thing below sign-out: nobody reaches
          it by accident on the way to something else. */}
      <DeleteAccountCard lang={lang} email={session.user.email ?? ""} />
    </div>
  );
}

// Account deletion. Hard and immediate — there is no grace window to explain,
// so the copy has one job: say plainly what disappears and what does not,
// BEFORE the control to do it appears. The two things that survive are the ones
// a person would otherwise be right to feel misled about: the payment record
// (kept for the books, with the name taken off) and the unsubscribe entry (kept
// so the mail they stopped stays stopped).
const DT: Record<Lang, Record<string, string>> = {
  ru: {
    title: "Удалить аккаунт",
    body: "Ваши результаты, отчёты, дети без своего аккаунта, согласия и приглашения будут удалены навсегда. Отменить это нельзя.",
    keeps:
      "Останутся: запись о платеже — без вашего имени, для бухгалтерии; и отметка об отписке от писем, чтобы они не начались снова. Ребёнок, у которого есть свой аккаунт, не удаляется — он просто перестанет быть привязан к вам.",
    start: "Удалить аккаунт",
    confirm_hint: "Введите свой email, чтобы подтвердить:",
    confirm: "Удалить навсегда",
    cancel: "Отмена",
    err_mismatch: "Email не совпадает с адресом аккаунта.",
    err_generic: "Не удалось удалить аккаунт. Попробуйте позже.",
    err_incomplete: "Данные удалены, но выход из аккаунта не завершился. Нажмите ещё раз.",
    busy: "Удаляем…",
  },
  az: {
    title: "Hesabı sil",
    body: "Nəticələriniz, hesabatlar, öz hesabı olmayan uşaqlar, razılıqlar və dəvətlər həmişəlik silinəcək. Bunu geri qaytarmaq olmur.",
    keeps:
      "Qalacaq: ödəniş qeydi — adınız olmadan, mühasibatlıq üçün; və məktublardan imtina qeydi ki, onlar yenidən başlamasın. Öz hesabı olan uşaq silinmir — sadəcə sizinlə bağlılığı kəsilir.",
    start: "Hesabı sil",
    confirm_hint: "Təsdiq üçün öz e-poçtunuzu yazın:",
    confirm: "Həmişəlik sil",
    cancel: "Ləğv et",
    err_mismatch: "E-poçt hesabın ünvanı ilə uyğun gəlmir.",
    err_generic: "Hesabı silmək alınmadı. Sonra yenidən cəhd edin.",
    err_incomplete: "Məlumatlar silindi, amma çıxış tamamlanmadı. Bir daha basın.",
    busy: "Silinir…",
  },
  en: {
    title: "Delete account",
    body: "Your results, reports, children without their own account, consents and invitations will be deleted permanently. This cannot be undone.",
    keeps:
      "What stays: the payment record — without your name, for the books; and the unsubscribe entry, so the mail you stopped stays stopped. A child who has their own account is not deleted — they simply stop being linked to you.",
    start: "Delete account",
    confirm_hint: "Type your email to confirm:",
    confirm: "Delete permanently",
    cancel: "Cancel",
    err_mismatch: "That is not this account's email address.",
    err_generic: "Could not delete the account. Please try again later.",
    err_incomplete: "The data is deleted, but signing out did not finish. Press again.",
    busy: "Deleting…",
  },
};

function DeleteAccountCard({ lang, email }: { lang: Lang; email: string }) {
  const t = DT[lang];
  const del = useServerFn(deleteMyAccount);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The typed address is checked again on the server against the JWT. Doing it
  // here as well is not the guard — it is what stops a typo from spending a
  // round trip on a request that cannot succeed.
  const matches = typed.trim().toLowerCase() === email.trim().toLowerCase();

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await del({ data: { confirmEmail: typed.trim() } });
      // The session belongs to an account that no longer exists; clear it
      // locally, then leave the cabinet by a full navigation — the landings
      // are static HTML outside the router.
      await supabase.auth.signOut().catch(() => undefined);
      window.location.assign(lang === "az" ? "/az" : lang === "en" ? "/en" : "/");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      setError(
        msg.includes("EMAIL_MISMATCH")
          ? t.err_mismatch
          : msg.includes("DELETE_INCOMPLETE")
            ? t.err_incomplete
            : t.err_generic,
      );
      setBusy(false);
    }
  };

  return (
    <div className="rounded-[18px] border border-destructive/30 bg-destructive/5 p-4 md:p-5">
      <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.title}</p>
      <p className="mt-1.5 text-sm leading-snug text-yt-ink/70">{t.body}</p>
      <p className="mt-2 text-xs leading-snug text-yt-ink/55">{t.keeps}</p>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-3 rounded-full border border-destructive/40 bg-white px-5 py-2.5 text-sm font-semibold text-destructive transition hover:bg-destructive/10"
        >
          {t.start}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <label htmlFor="delete-confirm" className="block text-xs text-yt-ink/60">
            {t.confirm_hint}
          </label>
          <Input
            id="delete-confirm"
            type="email"
            autoComplete="off"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={email}
            disabled={busy}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={run}
              disabled={!matches || busy}
              className="rounded-full bg-destructive px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-40"
            >
              {busy ? t.busy : t.confirm}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setTyped("");
                setError(null);
              }}
              disabled={busy}
              className={`${outlinePill} disabled:opacity-50`}
            >
              {t.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// B5 — invite a child to log in. Primary action is "Copy invite link" (parent
// shares via a messenger). Gated on child-data consent: on CONSENT_REQUIRED the
// control routes the parent through recordConsent(child_data_processing), then
// retries. Email delivery is a later, optional second channel (needs SMTP).
function InviteControl({ lang, childId }: { lang: Lang; childId: string }) {
  const t = CT[lang];
  const invite = useServerFn(createChildInvite);
  const consent = useServerFn(recordConsent);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [needConsent, setNeedConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { code, locale } = await invite({ data: { childId } });
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      // Use the parent's authoritative locale (from the server) for ?lang.
      setLink(`${origin}/cabinet/join?code=${code}&lang=${locale}`);
      setCopied(false);
      setNeedConsent(false);
    } catch (e) {
      if (e instanceof Error && e.message === "CONSENT_REQUIRED") setNeedConsent(true);
    } finally {
      setBusy(false);
    }
  };

  const giveConsent = async () => {
    setBusy(true);
    try {
      await consent({ data: { purpose: "child_data_processing", childId, locale: lang } });
      await generate();
    } catch {
      // keep the consent prompt on transient failure
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // clipboard blocked — the link is visible for manual copy
    }
  };

  if (link) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Input value={link} readOnly className="flex-1 text-xs" />
          <Button size="sm" onClick={copy}>
            {copied ? t.invite_copied : t.invite_copy}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t.invite_hint}</p>
        <Button size="sm" variant="ghost" onClick={generate} disabled={busy}>
          {busy ? "…" : t.invite_new}
        </Button>
      </div>
    );
  }
  if (needConsent) {
    return (
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">{t.invite_consent_needed}</p>
        <Button size="sm" variant="outline" onClick={giveConsent} disabled={busy}>
          {busy ? "…" : t.invite_consent_btn}
        </Button>
      </div>
    );
  }
  return (
    <Button size="sm" variant="outline" onClick={generate} disabled={busy}>
      {busy ? "…" : t.invite}
    </Button>
  );
}

// Guardian direction — a self-registered STUDENT invites their PARENT. Mirrors
// InviteControl (copyable link) but with NO consent gate (the parent consents on
// redeem) and the link carries &role=guardian for the join screen.
function GuardianInviteControl({ lang, childId }: { lang: Lang; childId: string }) {
  const t = CT[lang];
  const invite = useServerFn(createGuardianInvite);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { code, locale } = await invite({ data: { childId } });
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      setLink(`${origin}/cabinet/join?code=${code}&lang=${locale}&role=guardian`);
      setCopied(false);
    } catch {
      // transient — the button stays for a retry
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // clipboard blocked — the link is visible for manual copy
    }
  };

  if (link) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Input value={link} readOnly className="flex-1 text-xs" />
          <Button size="sm" onClick={copy}>
            {copied ? t.invite_copied : t.invite_copy}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t.guardian_hint}</p>
        <Button size="sm" variant="ghost" onClick={generate} disabled={busy}>
          {busy ? "…" : t.invite_new}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{t.guardian_desc}</p>
      <Button size="sm" variant="outline" onClick={generate} disabled={busy}>
        {busy ? "…" : t.invite_parent}
      </Button>
    </div>
  );
}

// B6a — achievements per child (no files). Add is gated on child-data consent
// (CONSENT_REQUIRED → recordConsent → retry). Parent or linked student can edit.
function AchievementsSection({
  lang,
  childId,
  showHeading = true,
}: {
  lang: Lang;
  childId: string;
  showHeading?: boolean;
}) {
  const t = CT[lang];
  const kindLabels = KIND_LABELS[lang];
  const fetchList = useServerFn(listAchievements);
  const add = useServerFn(addAchievement);
  const consent = useServerFn(recordConsent);
  const update = useServerFn(updateAchievement);
  const del = useServerFn(softDeleteAchievement);
  const [items, setItems] = useState<Achievement[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [needConsent, setNeedConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const emptyForm = { kind: "certificate", title: "", issuer: "", occurredAt: "", result: "" };
  const [form, setForm] = useState(emptyForm);

  const closeForm = () => {
    setForm(emptyForm);
    setAdding(false);
    setEditingId(null);
    setNeedConsent(false);
  };
  const openEdit = (a: Achievement) => {
    setForm({
      kind: a.kind,
      title: a.title,
      issuer: a.issuer ?? "",
      occurredAt: a.occurred_at ?? "",
      result: a.result ?? "",
    });
    setEditingId(a.id);
    setAdding(true);
  };

  const reload = () => {
    fetchList({ data: { childId } })
      .then((r) => setItems(r.achievements))
      .catch(() => setItems([]));
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const submitForm = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      if (editingId) {
        await update({
          data: {
            id: editingId,
            kind: form.kind,
            title: form.title,
            issuer: form.issuer || null,
            occurredAt: form.occurredAt || null,
            result: form.result || null,
          },
        });
      } else {
        await add({
          data: {
            childId,
            kind: form.kind,
            title: form.title,
            issuer: form.issuer || undefined,
            occurredAt: form.occurredAt || undefined,
            result: form.result || undefined,
          },
        });
      }
      closeForm();
      reload();
    } catch (e) {
      if (e instanceof Error && e.message === "CONSENT_REQUIRED") setNeedConsent(true);
    } finally {
      setBusy(false);
    }
  };

  const giveConsent = async () => {
    setBusy(true);
    try {
      await consent({ data: { purpose: "child_data_processing", childId, locale: lang } });
      setNeedConsent(false);
      await submitForm();
    } catch {
      // keep the consent prompt on transient failure
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setItems((cur) => (cur ?? []).filter((a) => a.id !== id));
    try {
      await del({ data: { id } });
    } catch {
      reload();
    }
  };

  return (
    <div className="space-y-2">
      {showHeading && <p className="text-xs font-medium text-muted-foreground">{t.ach_title}</p>}
      {items && items.length > 0 && (
        <ul className="space-y-1">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-start justify-between gap-2 rounded-md bg-muted/40 p-2 text-xs"
            >
              <div>
                <span className="font-medium">{a.title}</span>
                <span className="text-muted-foreground"> · {kindLabels[a.kind] ?? a.kind}</span>
                {a.issuer && <div className="text-muted-foreground">{a.issuer}</div>}
                {(a.occurred_at || a.result) && (
                  <div className="text-muted-foreground">
                    {[a.occurred_at, a.result].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(a)}
                  className="text-muted-foreground hover:text-primary"
                  aria-label="edit"
                >
                  ✎
                </button>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label={t.ach_delete}
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {needConsent ? (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{t.invite_consent_needed}</p>
          <Button size="sm" variant="outline" onClick={giveConsent} disabled={busy}>
            {busy ? "…" : t.invite_consent_btn}
          </Button>
        </div>
      ) : adding ? (
        <div className="space-y-2 rounded-md border border-border p-2">
          <select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {kindLabels[k]}
              </option>
            ))}
          </select>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t.ach_name}
          />
          <Input
            value={form.issuer}
            onChange={(e) => setForm({ ...form, issuer: e.target.value })}
            placeholder={t.ach_issuer}
          />
          <div className="flex gap-2">
            <Input
              type="date"
              value={form.occurredAt}
              onChange={(e) => setForm({ ...form, occurredAt: e.target.value })}
              className="flex-1"
            />
            <Input
              value={form.result}
              onChange={(e) => setForm({ ...form, result: e.target.value })}
              placeholder={t.ach_result}
              className="flex-1"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submitForm} disabled={busy || !form.title.trim()}>
              {busy ? "…" : t.save}
            </Button>
            <Button size="sm" variant="ghost" onClick={closeForm} disabled={busy}>
              {t.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setForm(emptyForm);
            setEditingId(null);
            setAdding(true);
          }}
        >
          {t.ach_add}
        </Button>
      )}
      {items && items.length === 0 && !adding && !needConsent && (
        <p className="text-xs text-muted-foreground">{t.ach_empty}</p>
      )}
    </div>
  );
}

// B0.5 — children list + "Add child" fallback (parent creates a child directly).
function ChildrenSection({
  lang,
  refreshKey,
  role,
}: {
  lang: Lang;
  refreshKey: number;
  role: string;
}) {
  const t = CT[lang];
  const fetchChildren = useServerFn(listChildren);
  const create = useServerFn(addChild);
  const eduOpen = useEduModuleOpen();
  const [children, setChildren] = useState<ChildRow[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  // Пустая строка = не выбрано. Отправить без выбора нельзя: возрастная группа
  // уходит в подбор и решает, проходит ли ребёнок по возрастному потолку
  // стипендии. Без неё отчёт молча теряет вариант.
  const [ageGroup, setAgeGroup] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () => {
    fetchChildren({ data: undefined as never })
      .then((r) => setChildren(r.children))
      .catch(() => setChildren([]));
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await create({ data: { displayName: n, ageGroup } });
      setName("");
      setAgeGroup("");
      setAdding(false);
      reload();
    } catch {
      // keep the form open on transient failure
    } finally {
      setBusy(false);
    }
  };

  // Account-type-adaptive: a self-registered STUDENT (their own child record,
  // no parent) sees an "invite parent" section — never the parent "Uşaqlar" /
  // add-child framing. A PARENT sees their children management.
  const parented = (children ?? []).filter((c) => c.is_parent);
  const ownStudent = (children ?? []).find((c) => c.is_student && !c.is_parent);

  return (
    <div className="space-y-5">
      {/* Student → invite their parent (bound to the student's OWN record). */}
      {ownStudent && (
        <div className="space-y-2">
          <p className="font-yt-serif text-lg font-semibold text-yt-ink">
            {t.guardian_section_title}
          </p>
          {ownStudent.has_parent ? (
            <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
              {t.badge_parent_linked}
            </span>
          ) : (
            <div className="rounded-xl border border-yt-line p-3">
              <GuardianInviteControl lang={lang} childId={ownStudent.id} />
            </div>
          )}
        </div>
      )}

      {/* Parent → children management (hidden for students — they parent no one). */}
      {role !== "student" && (
        <div className="space-y-3">
          <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.children}</p>
          {children !== null && parented.length === 0 && (
            <>
              <p className="text-sm text-yt-ink/55">{t.children_empty}</p>
              {/* Кабинет без ребёнка: вход в бесплатный подбор вузов (аудит 12.10) —
                  гостевой, ребёнок для него не нужен. */}
              <a
                href={ABROAD_GUEST_START_PATH[lang]}
                className="inline-flex min-h-11 items-center text-sm font-semibold text-yt-ink underline underline-offset-4"
                data-cta="cabinet_empty_to_abroad"
              >
                {ABROAD_ENTRY_COPY[lang]}
              </a>
            </>
          )}
          {parented.length > 0 && (
            <ul className="space-y-2">
              {parented.map((c) => (
                <li key={c.id} className="rounded-xl border border-yt-line p-3 text-sm space-y-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span>{c.display_name}</span>
                    {c.age_group && (
                      <span className="text-xs text-muted-foreground">
                        {c.age_group.replace(/_/g, "–")}
                      </span>
                    )}
                  </div>
                  {c.has_student ? (
                    <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      {t.badge_linked}
                    </span>
                  ) : (
                    <InviteControl lang={lang} childId={c.id} />
                  )}
                  {/* Анкета «Образование за рубежом» открывалась только по
                      прямому адресу — отсюда до неё не было пути. Ссылки
                      появляются, когда справочник стран опубликован: см.
                      useEduModuleOpen. */}
                  {eduOpen && (
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={`/cabinet/edu?child=${c.id}`}
                        className="inline-block text-xs font-semibold text-primary underline"
                      >
                        Образование за рубежом →
                      </a>
                      <a
                        href={`/cabinet/edu-report?child=${c.id}`}
                        className="inline-block text-xs font-semibold text-primary underline"
                      >
                        Отчёт по подбору →
                      </a>
                      <a
                        href={`/cabinet/edu-consult?child=${c.id}`}
                        className="inline-block text-xs font-semibold text-primary underline"
                      >
                        Консультация →
                      </a>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {adding ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.child_name}
                className="flex-1"
              />
              <select
                value={ageGroup}
                onChange={(e) => setAgeGroup(e.target.value)}
                aria-label={t.age_label}
                className="h-9 rounded-md border border-yt-line bg-white px-2 text-sm"
              >
                <option value="">{t.age_label}</option>
                {["10_11", "12_13", "14_15", "16_18", "18_22"].map((g) => (
                  <option key={g} value={g}>
                    {g.replace(/_/g, "–")}
                  </option>
                ))}
              </select>
              <Button size="sm" onClick={submit} disabled={busy || !name.trim() || !ageGroup}>
                {busy ? "…" : t.save}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)} disabled={busy}>
                {t.cancel}
              </Button>
              <p className="w-full text-xs text-yt-ink/55">{t.age_hint}</p>
            </div>
          ) : (
            <button type="button" onClick={() => setAdding(true)} className={outlinePill}>
              {t.add_child}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// D1 — Achievements tab: per-child achievements (each child's AchievementsSection),
// or a rich empty state. Achievements are child-scoped, so a childless account
// (no test claimed, not in a class yet) sees the invitation to get started.
function AchievementsTab({ lang, refreshKey }: { lang: Lang; refreshKey: number }) {
  const t = CT[lang];
  const fetchChildren = useServerFn(listChildren);
  const [children, setChildren] = useState<ChildRow[] | null>(null);
  useEffect(() => {
    let active = true;
    fetchChildren({ data: undefined as never })
      .then((r) => {
        if (active) setChildren(r.children);
      })
      .catch(() => {
        if (active) setChildren([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  if (children === null) {
    return <div className="h-40 animate-pulse rounded-2xl border border-yt-line bg-white" />;
  }
  if (children.length === 0) {
    return (
      <EmptyState
        icon={Award}
        title={t.empty_ach_title}
        body={t.empty_ach_body}
        action={
          <Link to="/start" className={amberPill}>
            {t.cta_take_test}
          </Link>
        }
      />
    );
  }
  // One child (the common case — a student is their own child): show their
  // achievements directly. Group under child names only when there's >1.
  if (children.length === 1) {
    return <AchievementsSection lang={lang} childId={children[0].id} showHeading={false} />;
  }
  return (
    <div className="space-y-4">
      {children.map((c) => (
        <div key={c.id} className="rounded-2xl border border-yt-line bg-white p-4">
          <p className="mb-3 font-semibold text-yt-ink">{c.display_name}</p>
          <AchievementsSection lang={lang} childId={c.id} showHeading={false} />
        </div>
      ))}
    </div>
  );
}

// D1 — class-code invitation card for the Profile tab. Explains up front what a
// teacher can and cannot see.
function SchoolCodeCard({ lang, primary = true }: { lang: Lang; primary?: boolean }) {
  const t = CT[lang];
  return (
    <div className="rounded-2xl border border-yt-line bg-yt-teal/5 p-5">
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-yt-teal/12 text-yt-teal">
          <Ticket className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.school_card_title}</p>
          <p className="mt-1 text-sm leading-relaxed text-yt-ink/60">{t.school_card_body}</p>
          <Link to="/redeem" className={`${primary ? amberPill : outlinePill} mt-4`}>
            {t.school_card_cta}
          </Link>
        </div>
      </div>
    </div>
  );
}

// Claim banner — hybrid flow: auto-surface unclaimed past sessions matching the
// caller's verified email; attach ONLY on explicit confirmation ("Добавить").
/**
 * Компактный список детей на вкладке результатов.
 *
 * Не дубль управления с «Профиля»: ни формы добавления, ни приглашений — там
 * им и место. Здесь один вопрос, на который вкладка обязана отвечать: «мой
 * ребёнок появился?»
 */
function ChildrenHere({
  lang,
  refreshKey,
  onTab,
}: {
  lang: Lang;
  refreshKey: number;
  onTab: (k: NavKey) => void;
}) {
  const t = CT[lang];
  const fetchChildren = useServerFn(listChildren);
  const eduOpen = useEduModuleOpen();
  const [children, setChildren] = useState<ChildRow[] | null>(null);

  useEffect(() => {
    let active = true;
    fetchChildren({ data: undefined as never })
      .then((r) => active && setChildren(r.children))
      .catch(() => active && setChildren([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const parented = (children ?? []).filter((c) => c.is_parent);
  // Пусто — молчим: пустой блок «Ваши дети» на экране новичка только мешает.
  if (!parented.length) return null;

  return (
    <section className="rounded-2xl border border-yt-line bg-white p-4 md:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.kids_here}</p>
        <button
          type="button"
          onClick={() => onTab("profile")}
          className="text-xs font-semibold text-yt-teal underline"
        >
          {t.kids_manage}
        </button>
      </div>
      <ul className="mt-3 flex flex-wrap gap-2">
        {parented.map((c) => (
          <li
            key={c.id}
            className="rounded-xl border border-yt-line bg-yt-cream/40 px-3 py-2 text-sm"
          >
            <span className="font-medium text-yt-ink">{c.display_name}</span>
            {c.age_group && (
              <span className="ml-2 text-xs text-yt-ink/55">{c.age_group.replace(/_/g, "–")}</span>
            )}
            {/* Дверь к отчёту прямо отсюда. До этого попасть в него можно было
                только зная адрес: анкета заканчивалась тупиковой карточкой, а
                в кабинете ссылки не было ни одной. */}
            {eduOpen && (
              <a
                href={`/cabinet/edu-report?child=${c.id}`}
                className="ml-3 text-xs font-semibold text-primary underline"
              >
                {t.kids_report}
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ClaimBanner({
  lang,
  onClaimed,
  neighborAmber = false,
}: {
  lang: Lang;
  onClaimed: () => void;
  // True when something else on this screen already owns the amber accent (the
  // Results empty-state CTA). Then the banner stays fully secondary.
  neighborAmber?: boolean;
}) {
  const t = CT[lang];
  const fetchCandidates = useServerFn(listClaimCandidates);
  const attach = useServerFn(claimSession);
  const [candidates, setCandidates] = useState<ClaimCandidate[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Per-candidate school-share consent (only shown/used for child_email + school).
  const [shareConsent, setShareConsent] = useState<Record<string, boolean>>({});
  // Which collapsed groups are expanded to show their extra attempts.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Что именно произошло при последней привязке. Не «готово», а с именем:
  // без него человек не знает, появился ли ребёнок и который.
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchCandidates({ data: undefined as never })
      .then((r) => {
        if (active) setCandidates(r.candidates);
      })
      .catch(() => {
        if (active) setCandidates([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Список опустел, но сообщение о последней привязке ещё не прочитано —
  // блок остаётся ради него. Иначе экран просто мигнёт и станет пустым.
  if (!candidates || (candidates.length === 0 && !done)) return null;

  const add = async (c: ClaimCandidate) => {
    setBusy(c.session_id);
    try {
      const res = await attach({
        data: { sessionId: c.session_id, schoolShareConsent: !!shareConsent[c.session_id] },
      });
      setCandidates((cur) => (cur ?? []).filter((x) => x.session_id !== c.session_id));
      // Слияние по имени законно, но должно быть громким: тест мог уйти к уже
      // существующему ребёнку, и нового в списке не появится.
      const kid = res?.child;
      if (kid) {
        const age = (kid.ageGroup ?? c.age_group ?? "").replace(/_/g, "–");
        setDone(
          (kid.merged ? t.claimed_merged : t.claimed_new)
            .replace("{name}", kid.name)
            .replace("{age}", age || "—"),
        );
      }
      onClaimed();
    } catch {
      // Keep the row on a transient failure — don't hide the option.
    } finally {
      setBusy(null);
    }
  };

  // Collapse duplicates: group by child + age group. The primary card per group
  // is the completed session (else the freshest); extra attempts fold under a
  // tap-to-expand toggle. Claiming a specific session still works per-row.
  const groupsMap = new Map<string, ClaimCandidate[]>();
  for (const c of candidates) {
    const key = `${c.child_name}||${c.age_group}`;
    const arr = groupsMap.get(key);
    if (arr) arr.push(c);
    else groupsMap.set(key, [c]);
  }
  const groups = Array.from(groupsMap.entries()).map(([key, list]) => {
    const sorted = [...list].sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? -1 : 1;
      return String(b.created_at).localeCompare(String(a.created_at));
    });
    return { key, primary: sorted[0], extras: sorted.slice(1) };
  });
  // One-amber rule, derived from the GROUPED list so it can't be broken by a
  // per-row prop later: amber is earned only when the banner shows exactly one
  // Add button — a single group with no folded attempts (expanding a group would
  // otherwise put a column of ambers on screen) — and only when no neighbouring
  // card already owns the accent. Every other case is secondary outline.
  const amber = !neighborAmber && groups.length === 1 && groups[0].extras.length === 0;
  const rowProps = (c: ClaimCandidate, primary: boolean) => ({
    c,
    lang,
    primary,
    busy: busy === c.session_id,
    onAdd: () => add(c),
    shareChecked: !!shareConsent[c.session_id],
    onShareChange: (v: boolean) => setShareConsent((s) => ({ ...s, [c.session_id]: v })),
  });

  return (
    <section className="rounded-2xl border border-yt-line bg-white p-4 md:p-5">
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-yt-teal/10 text-yt-teal">
          <FileText className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.claim_title}</p>
          <p className="mt-0.5 text-sm text-yt-ink/60">{t.claim_body}</p>
        </div>
      </div>
      {done && (
        <p
          role="status"
          className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-900"
        >
          {done}
        </p>
      )}
      <ul className="mt-4 space-y-2">
        {groups.map((g) => {
          const isOpen = !!expanded[g.key];
          return (
            <li
              key={g.key}
              className="space-y-2 rounded-xl border border-yt-line bg-yt-cream/40 p-3"
            >
              <ClaimRow {...rowProps(g.primary, amber)} />
              {g.extras.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))}
                    aria-expanded={isOpen}
                    className="flex min-h-[44px] items-center gap-1 text-xs font-semibold text-yt-teal"
                  >
                    <ChevronDown
                      className={`size-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                    {isOpen ? t.more_hide : t.more_show.replace("{n}", String(g.extras.length))}
                  </button>
                  {isOpen && (
                    <ul className="space-y-2 border-t border-yt-line pt-2">
                      {g.extras.map((c) => (
                        <li key={c.session_id}>
                          <ClaimRow {...rowProps(c, false)} />
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// One claimable session — name + age, completed/not status, and the Add action
// (amber when it's the screen's single primary, else secondary outline). Used
// for both the collapsed primary and the expanded extra attempts.
function ClaimRow({
  c,
  lang,
  primary,
  busy,
  onAdd,
  shareChecked,
  onShareChange,
}: {
  c: ClaimCandidate;
  lang: Lang;
  primary: boolean;
  busy: boolean;
  onAdd: () => void;
  shareChecked: boolean;
  onShareChange: (v: boolean) => void;
}) {
  const t = CT[lang];
  // Disclosure to a school is offered only where it can legitimately be given.
  // A minor cannot hand over their own result with a checkbox — the parent
  // confirms it, after the student invites them from the cabinet — so the
  // control is replaced by the explanation of what has to happen first, rather
  // than shown greyed out with no way forward.
  const schoolCandidate = c.matched_field === "child_email" && !!c.school_name;
  const minor = isMinorBand(c.age_group);
  const showShare = schoolCandidate && !minor;
  const showShareBlocked = schoolCandidate && minor;
  const shareLabel = {
    ru: (name: string) => `Согласен(на), что школа «${name}» видит мой индивидуальный результат`,
    az: (name: string) => `«${name}» məktəbinin mənim fərdi nəticəmi görməsinə razıyam`,
    en: (name: string) => `I agree that the school “${name}” can see my individual result`,
  }[lang];
  const shareBlockedLabel = {
    ru: (name: string) =>
      `Чтобы школа «${name}» увидела твой результат, нужно согласие родителя. Пригласи родителя в кабинете — после подтверждения согласие можно будет дать.`,
    az: (name: string) =>
      `«${name}» məktəbinin nəticəni görməsi üçün valideyn razılığı lazımdır. Kabinetdə valideynini dəvət et — təsdiqdən sonra razılıq vermək mümkün olacaq.`,
    en: (name: string) =>
      `For the school “${name}” to see your result, a parent has to agree. Invite your parent from the cabinet — once they confirm, the consent can be given.`,
  }[lang];
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-yt-ink">
            {c.child_name || t.result}
            {c.age_group ? (
              <span className="font-normal text-yt-ink/50">
                {" "}
                · {c.age_group.replace(/_/g, "–")}
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 text-xs">
            {c.completed ? (
              <span className="inline-flex items-center gap-1 text-yt-mint">
                <CheckCircle2 className="size-3.5" /> {t.status_done}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-yt-ink/45">
                <Clock className="size-3.5" /> {t.status_not_done}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={busy}
          className={`${primary ? amberPill : outlinePill} shrink-0 disabled:opacity-50`}
        >
          {busy ? "…" : t.add}
        </button>
      </div>
      {showShare && (
        <label className="mt-2 flex items-start gap-2 text-xs text-yt-ink/60">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-yt-teal"
            checked={shareChecked}
            onChange={(e) => onShareChange(e.target.checked)}
          />
          <span>{shareLabel(c.school_name as string)}</span>
        </label>
      )}
      {showShareBlocked && (
        <p className="mt-2 text-xs leading-snug text-yt-ink/55">
          {shareBlockedLabel(c.school_name as string)}
        </p>
      )}
    </div>
  );
}

// «Мой путь» — snapshots for the caller (OWN + their children's, as parent or
// student) via the getMyPath server-fn. This is what lets a linked student see
// their child's result while an admin still sees only their own here.

// Humanized labels. Phase A cabinet chrome is RU-only, so labels are RU here;
// full az/en localization lands when the cabinet is internationalized.
// Sources of truth (kept in sync manually — small, stable sets):
//   ARCHETYPE_LABELS_RU ← reports1213.ts ARCHETYPE_NAMES (Latin-keyed here vs
//     its Cyrillic keys; ARCHETYPE_CODES are Latin A/I/S/K/L/E/P/B).
//   RIASEC_LABELS_RU    ← reports1822.ts RIASEC_LABELS.ru.
// Archetype code → localized name. Source of truth: report_archetypes
// .content_{ru,az}.name (en supplied here — there is no content_en column).
// Small, stable set (8 codes).
const ARCHETYPE_TITLES: Record<string, Record<Lang, string>> = {
  A: { ru: "Аналитик", az: "Analitik", en: "Analyst" },
  I: { ru: "Исследователь", az: "Tədqiqatçı", en: "Explorer" },
  S: { ru: "Созидатель", az: "Yaradıcı", en: "Creator" },
  K: { ru: "Коммуникатор", az: "Kommunikator", en: "Communicator" },
  L: { ru: "Лидер-организатор", az: "Lider-təşkilatçı", en: "Leader-Organizer" },
  E: { ru: "Эмпат", az: "Empat", en: "Empath" },
  P: { ru: "Практик-экспериментатор", az: "Praktik-eksperimentator", en: "Practical Experimenter" },
  B: { ru: "Предприниматель", az: "Sahibkar", en: "Entrepreneur" },
};
// RIASEC letter → name stays RU-only for now (14-15 / 18-22 bands). Localizing
// it needs the RIASEC label source — a separate content task.
const RIASEC_LABELS_RU: Record<string, string> = {
  R: "Реалистичный",
  I: "Исследовательский",
  A: "Артистический",
  S: "Социальный",
  E: "Предпринимательский",
  C: "Конвенциональный",
};

function formatAgeGroup(ag: string | null, lang: Lang): string {
  if (!ag) return "";
  return `${ag.replace(/_/g, "–")} ${CT[lang].years}`;
}
export function archetypeName(code: string, lang: Lang): string {
  return ARCHETYPE_TITLES[code]?.[lang] ?? code;
}
function archetypeTitle(primary: string, secondary: string | null, lang: Lang): string {
  const p = archetypeName(primary, lang);
  if (!secondary) return p;
  return `${p} · ${archetypeName(secondary, lang)}`;
}
function riasecTitle(code: string): string {
  return code
    .split("")
    .map((l) => RIASEC_LABELS_RU[l] ?? l)
    .join(" · ");
}
function snapshotTitle(r: PathSnapshot, lang: Lang): string {
  if (r.archetype_primary) return archetypeTitle(r.archetype_primary, r.archetype_secondary, lang);
  if (r.riasec_code) return riasecTitle(r.riasec_code);
  return CT[lang].result;
}

// Always-available way into a test. It used to exist only inside empty states,
// so anyone who already had a result had to go back to the landing — where the
// session is created ownerless and later needs the claim banner to find its
// owner. Started from here (signed in), the session is bound on creation.
//
// Driven by a list on purpose: the second assessment (studying abroad) becomes
// one more entry, not a rewrite. The first entry is the screen's primary
// action (amber, dark text); any further one is secondary by construction, so
// the one-amber rule can't be broken by adding to the array.
// The assessments TestEntry offers. Module scope so the results tab can ask
// what the card will render without duplicating the rule — see
// testEntryRendersAmber(). The second assessment (studying abroad) is one more
// entry here.
const TEST_ENTRIES: { key: string; to: string }[] = [{ key: "career", to: "/start" }];

// TestEntry paints its FIRST action amber, so it contributes an amber to the
// screen exactly when it has something to offer. Derived, not asserted: empty
// the list or drop the card and the answer follows by itself.
function testEntryRendersAmber(): boolean {
  return TEST_ENTRIES.length > 0;
}

function TestEntry({ lang }: { lang: Lang }) {
  const t = CT[lang];
  if (TEST_ENTRIES.length === 0) return null;
  const tests = TEST_ENTRIES.map((x) => ({
    ...x,
    label: t.cta_take_test,
    body: t.test_entry_body,
  }));
  return (
    <section className="rounded-2xl border border-yt-line bg-white p-4 md:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-yt-serif text-lg font-semibold text-yt-ink">{t.test_entry_title}</p>
          <p className="mt-0.5 text-sm text-yt-ink/60">{tests[0].body}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {tests.map((x, i) => (
            <Link
              key={x.key}
              to={x.to}
              className={`${i === 0 ? amberPill : outlinePill} min-h-[44px]`}
            >
              {x.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function MyPath({ refreshKey, lang }: { refreshKey: number; lang: Lang }) {
  const t = CT[lang];
  const fetchPath = useServerFn(getMyPath);
  const [rows, setRows] = useState<PathSnapshot[] | null>(null);

  useEffect(() => {
    let active = true;
    fetchPath({ data: undefined as never })
      .then((r) => {
        if (active) setRows(r.snapshots);
      })
      .catch(() => {
        if (active) setRows([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Results tab leads with the header; result snapshots render directly as a
  // card grid here (no intermediate "Мой путь" heading — that's its own tab).
  if (rows === null) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-2xl border border-yt-line bg-white" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    // No CTA here — TestEntry sits right above with the same action, and two
    // ambers on one screen is exactly what the one-amber rule forbids.
    return <EmptyState icon={Sparkles} title={t.empty_results_title} body={t.empty_results_body} />;
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((r) => {
        const accent = r.color_hex ?? "#1E5A6B";
        // Stored skills are machine keys; translate them and drop anything
        // outside the dictionary. Printing keys raw is how "analysis" ended up
        // on screen, in English, under an Azerbaijani interface.
        const skills = skillLabels(r.top_skills, lang).slice(0, 3).join(" · ");
        const inner = (
          <>
            <div className="flex items-start gap-3">
              <span
                className="flex size-10 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${accent}1a`, color: accent }}
              >
                <Sparkles className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-yt-ink">{snapshotTitle(r, lang)}</p>
                {r.age_group ? (
                  <p className="mt-0.5 text-xs text-yt-ink/55">
                    {formatAgeGroup(r.age_group, lang)}
                  </p>
                ) : null}
              </div>
            </div>
            {skills ? <p className="mt-3 text-xs text-yt-ink/55">{skills}</p> : null}
            {r.access_token ? (
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-yt-teal">
                {t.open_report} <span aria-hidden>→</span>
              </span>
            ) : null}
          </>
        );
        // Whole card is the report link (archetype-colored left edge + tinted
        // icon signal the archetype; the teal "open report →" makes the target
        // explicit). Falls back to a static card if the token is missing.
        return r.access_token ? (
          <Link
            key={r.id}
            to="/session/$token"
            params={{ token: r.access_token }}
            // Open the report in the ACCOUNT's language, not the language the
            // test happened to be taken in. The caller owns this choice — the
            // report route has no way to tell a cabinet visit from an
            // anonymous one, and an anonymous visit must keep language_key.
            search={{ lang }}
            aria-label={`${t.open_report}: ${snapshotTitle(r, lang)}`}
            className="flex flex-col rounded-2xl border border-l-4 border-yt-line bg-white p-4 transition hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yt-teal focus-visible:ring-offset-2 motion-safe:active:scale-[0.99]"
            style={{ borderLeftColor: accent }}
          >
            {inner}
          </Link>
        ) : (
          <div
            key={r.id}
            className="flex flex-col rounded-2xl border border-l-4 border-yt-line bg-white p-4"
            style={{ borderLeftColor: accent }}
          >
            {inner}
          </div>
        );
      })}
    </div>
  );
}

// ─── Плитки владельца ─────────────────────────────────────────────────
//
// Три ТЕСТА продукта по выбранной школе, одна цифра на плитке, две кнопки.
// Видит только user_roles.role = admin. Русский язык: экран для одного
// владельца, три языка здесь лишняя работа (переведём, когда плитки пойдут
// к школам). Ничего сверх этого в первой версии — принцип «максимально
// просто для того, кто пользуется».

/** Прочитать, admin ли пользователь. null — ещё не знаем. */
function useIsAdmin(userId: string): boolean | null {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      if (alive) setIsAdmin(!!data);
    })();
    return () => {
      alive = false;
    };
  }, [userId]);
  return isAdmin;
}

const EAS_NAME = "European Azerbaijan School";

function OwnerTilesScreen({
  onSignOut,
  signingOut,
}: {
  onSignOut: () => void;
  signingOut: boolean;
}) {
  const fetchSchools = useServerFn(listSchools);
  const fetchTiles = useServerFn(getOwnerTiles);
  const [schools, setSchools] = useState<{ id: string; name: string }[]>([]);
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [tiles, setTiles] = useState<OwnerTiles | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Список школ + выбор по умолчанию European Azerbaijan School.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetchSchools({});
        const rows = (res.rows ?? []).map((s) => ({ id: String(s.id), name: String(s.name) }));
        if (!alive) return;
        setSchools(rows);
        const eas = rows.find((s) => s.name === EAS_NAME);
        setSchoolId(eas?.id ?? rows[0]?.id ?? null);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [fetchSchools]);

  // Цифры выбранной школы.
  useEffect(() => {
    if (!schoolId) return;
    let alive = true;
    setTiles(null);
    // Ошибка прошлой школы не должна залипать на новой.
    setError(null);
    void (async () => {
      try {
        const res = await fetchTiles({ data: { school_id: schoolId } });
        if (alive) setTiles(res);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [schoolId, fetchTiles]);

  return (
    <div className="min-h-screen bg-yt-cream text-yt-ink font-yt-sans">
      <header className="sticky top-0 z-20 border-b border-yt-line bg-yt-cream/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center gap-3 px-4 md:px-6">
          <span className="font-yt-serif text-lg font-semibold text-yt-teal">Yolutap</span>
          {/* Переключатель школы — в шапке, как договорено. */}
          <select
            value={schoolId ?? ""}
            onChange={(e) => setSchoolId(e.target.value || null)}
            className="ml-2 min-h-[40px] rounded-full border border-yt-line bg-white px-3 text-sm text-yt-ink"
            aria-label="Школа"
          >
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            aria-label="Выйти"
            className="ml-auto flex items-center gap-2 rounded-full px-4 py-2 text-sm text-yt-ink/60 disabled:opacity-50"
          >
            <LogOut className="size-4" />
            {signingOut ? "…" : "Выйти"}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1120px] px-4 pb-10 pt-6 md:px-6 md:pt-10">
        {error ? (
          <p className="text-sm font-semibold text-red-700">{error}</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <OwnerTile
              title="Карьера"
              figure={tiles ? `${tiles.career.tested} из ${tiles.career.invited} прошли` : "…"}
              resultsHref="/admin"
              selfTest={<OwnerCareerSelfTest />}
            />
            <OwnerTile
              title="Антибуллинг"
              figure={tiles ? antibullyingFigure(tiles.antibullying) : "…"}
              resultsHref="/admin"
            />
            <OwnerTile
              title="Учёба за рубежом"
              figure={
                tiles
                  ? `${tiles.edu.completed} ${ruAnkety(tiles.edu.completed)}`
                  : "…"
              }
              resultsHref="/cabinet/edu-report"
              selfTest={<OwnerEduSelfTest />}
            />
          </div>
        )}
      </main>
    </div>
  );
}

function antibullyingFigure(a: OwnerTiles["antibullying"]): string {
  if (a.state === "no_wave") return "опрос не идёт";
  return `${a.done} из ${a.total} ${ruClasses(a.total)} прошли опрос`;
}

/**
 * «из 1 класса / из 2 классов / из 21 класса» — после «из» родительный
 * падеж: единственное число только на 1 (кроме 11), иначе множественное.
 */
function ruClasses(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "класса";
  return "классов";
}

/** «1 анкета / 2 анкеты / 5 анкет». */
function ruAnkety(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "анкета";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "анкеты";
  return "анкет";
}

/**
 * «Пройти самому» для карьеры: владелец выбирает возраст и проходит тест как
 * пробный ребёнок. Сессия помечается origin = owner_test на сервере
 * (startOwnerTest) и не входит ни в одну цифру.
 */
function OwnerCareerSelfTest() {
  const start = useServerFn(startOwnerTest);
  const navigate = useNavigate();
  const [age, setAge] = useState<"10_11" | "12_13" | "14_15" | "16_18" | "18_22">("14_15");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      const { access_token } = await start({ data: { age_group: age } });
      navigate({ to: "/session/$token", params: { token: access_token } });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <select
          value={age}
          onChange={(e) => setAge(e.target.value as typeof age)}
          disabled={busy}
          aria-label="Возраст"
          className="min-h-[44px] rounded-full border border-yt-line bg-white px-3 text-sm text-yt-ink"
        >
          <option value="10_11">10–11</option>
          <option value="12_13">12–13</option>
          <option value="14_15">14–15</option>
          <option value="16_18">16–18</option>
          <option value="18_22">18+</option>
        </select>
        <button
          type="button"
          onClick={() => void go()}
          disabled={busy}
          className="min-h-[44px] flex-1 rounded-full border border-yt-teal px-4 text-sm font-semibold text-yt-teal disabled:opacity-50"
        >
          {busy ? "…" : "Пройти самому →"}
        </button>
      </div>
      {error && <p className="text-xs font-semibold text-red-700">{error}</p>}
    </div>
  );
}

/**
 * «Пройти самому» для учёбы за рубежом: сервер заводит пробного ребёнка, и
 * владелец идёт по пути семьи — /cabinet/edu?child=… Анкета не входит в цифру
 * плитки (created_by = admin).
 */
function OwnerEduSelfTest() {
  const start = useServerFn(startOwnerEduTest);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      const { child_id } = await start({});
      navigate({ to: "/cabinet/edu", search: { child: child_id } });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => void go()}
        disabled={busy}
        className="min-h-[44px] rounded-full border border-yt-teal px-4 text-sm font-semibold text-yt-teal disabled:opacity-50"
      >
        {busy ? "…" : "Пройти самому →"}
      </button>
      {error && <p className="text-xs font-semibold text-red-700">{error}</p>}
    </div>
  );
}

function OwnerTile({
  title,
  figure,
  resultsHref,
  selfTest,
}: {
  title: string;
  figure: string;
  resultsHref: string;
  /** Активная «Пройти самому». Нет — кнопка видна, но неактивна. */
  selfTest?: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-yt-line bg-white p-5">
      <h2 className="font-yt-serif text-lg font-semibold text-yt-ink">{title}</h2>
      <p className="text-2xl font-semibold tabular-nums text-yt-ink">{figure}</p>
      <div className="mt-auto flex flex-col gap-2">
        <a
          href={resultsHref}
          className="min-h-[44px] rounded-full bg-yt-teal px-4 text-center text-sm font-semibold leading-[44px] text-white"
        >
          Результаты
        </a>
        {/* «Пройти самому» активна у карьеры и учёбы за рубежом. У
            антибуллинга выключена: опрос анонимный, метку ставить некуда. */}
        {selfTest ?? (
          <button
            type="button"
            disabled
            className="min-h-[44px] cursor-not-allowed rounded-full border border-yt-line px-4 text-sm text-yt-ink/40"
            title="Скоро"
          >
            Пройти самому →
          </button>
        )}
      </div>
    </section>
  );
}
